import React, {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {apiDelete, apiGet, apiPatch, apiPost} from "../api/apiClient";
import MarkdownText from "../components/MarkdownText";
import "./AdminPage.css";

const DASHBOARD_COURSE_SESSION_KEY = "gamifyit:selectedInstructorDashboardCourseId";
const TOPIC_ADMIN_COURSE_SESSION_KEY = "gamifyit:topicAdminCourseId";
const QUESTION_BANK_TOPIC_SESSION_KEY = "gamifyit:questionBankTopicId";
const QUESTION_BANK_COVERAGE_COURSE_SESSION_KEY = "gamifyit:questionBankCoverageCourseId";
const USER_ACTIVITY_USER_SESSION_KEY = "gamifyit:userActivityUserId";
const BANK_FILTER_SESSION_PREFIX = "gamifyit:bankFilters:";

function readDashboardCourseSession() {
    if (typeof window === "undefined") return "";
    try {
        return window.sessionStorage.getItem(DASHBOARD_COURSE_SESSION_KEY) || "";
    } catch {
        return "";
    }
}

function writeDashboardCourseSession(courseId) {
    if (typeof window === "undefined") return;
    try {
        if (courseId) window.sessionStorage.setItem(DASHBOARD_COURSE_SESSION_KEY, String(courseId));
        else window.sessionStorage.removeItem(DASHBOARD_COURSE_SESSION_KEY);
    } catch {
        // Ignore storage failures so dashboard selection still works normally.
    }
}

function readSessionValue(key) {
    if (typeof window === "undefined") return "";
    try {
        return window.sessionStorage.getItem(key) || "";
    } catch {
        return "";
    }
}

function writeSessionValue(key, value) {
    if (typeof window === "undefined") return;
    try {
        if (value) window.sessionStorage.setItem(key, String(value));
        else window.sessionStorage.removeItem(key);
    } catch {
        // Ignore storage failures so filters still work during the current session.
    }
}

function readBankFiltersSession(bankType = "") {
    if (!bankType || typeof window === "undefined") {
        return {course_id: "", topic_id: "", activity_type: "", question_kind: ""};
    }
    try {
        const parsed = JSON.parse(window.sessionStorage.getItem(`${BANK_FILTER_SESSION_PREFIX}${bankType}`) || "{}");
        return {
            course_id: parsed.course_id || "",
            topic_id: parsed.topic_id || "",
            activity_type: parsed.activity_type || "",
            question_kind: parsed.question_kind || "",
        };
    } catch {
        return {course_id: "", topic_id: "", activity_type: "", question_kind: ""};
    }
}

function writeBankFiltersSession(bankType = "", filters = {}) {
    if (!bankType || typeof window === "undefined") return;
    try {
        window.sessionStorage.setItem(`${BANK_FILTER_SESSION_PREFIX}${bankType}`, JSON.stringify({
            course_id: filters.course_id || "",
            topic_id: filters.topic_id || "",
            activity_type: filters.activity_type || "",
            question_kind: filters.question_kind || "",
        }));
    } catch {
        // Ignore storage failures so filters still work during the current session.
    }
}

const COURSE_ITEM = {
    label: "Course",
    path: "/courseadmin",
    resource: "courses",
    idKey: "course_id",
    canAdd: true,
    canDelete: true,
    description: "Create, update, or soft-delete course records.",
    tableColumns: [
        {key: "course_code", label: "Code"},
        {key: "course_name", label: "Course Name"},
        {key: "instructor_names", label: "Instructors"},
        {key: "semester", label: "Semester"},
        {key: "location", label: "Location"},
        {key: "individual_mc_question_seconds", label: "MC/Q"},
        {key: "pre_test_question_seconds", label: "Pre/Q"},
        {key: "post_test_question_seconds", label: "Post/Q"},
        {key: "individual_case_seconds", label: "Case"},
        {key: "group_activity_seconds", label: "Group"},
        {key: "quiz_question_seconds", label: "Quiz/Q"},
    ],
    fields: [
        {key: "course_code", label: "Course Code", required: true},
        {key: "course_name", label: "Course Name", required: true},
        {key: "instructor_id", label: "Instructor 1", type: "select", reference: "instructors"},
        {key: "instructor2_id", label: "Instructor 2", type: "select", reference: "instructors"},
        {key: "semester", label: "Semester", type: "number"},
        {key: "location", label: "Location"},
        {key: "individual_mc_question_seconds", label: "Individual MC Seconds / Question", type: "number", placeholder: "15"},
        {key: "pre_test_question_seconds", label: "Pre-test Seconds / Question", type: "number", placeholder: "15"},
        {key: "post_test_question_seconds", label: "Post-test Seconds / Question", type: "number", placeholder: "15"},
        {key: "individual_case_seconds", label: "Individual Case Seconds", type: "number", placeholder: "240"},
        {key: "group_activity_seconds", label: "Group Activity Seconds", type: "number", placeholder: "600"},
        {key: "quiz_question_seconds", label: "Quiz Seconds / Question", type: "number", placeholder: "15"},
    ],
};

const TOPIC_ITEM = {
    label: "Topics",
    path: "/topicadmin",
    resource: "topics",
    idKey: "topic_id",
    canAdd: true,
    canDelete: true,
    description: "Create, update, or soft-delete topics for each active course.",
    tableColumns: [
        {key: "course_name", label: "Course"},
        {key: "week", label: "Week"},
        {key: "topic_name", label: "Topic Name"},
        {key: "show_topic", label: "Visible", type: "boolean"},
        {key: "is_active", label: "Active Dashboard Topic", type: "boolean"},
        {key: "pre_test_start_at", label: "Pre-test Start", type: "datetime"},
        {key: "pre_test_end_at", label: "Pre-test End", type: "datetime"},
        {key: "post_test_start_at", label: "Post-test Start", type: "datetime"},
        {key: "post_test_end_at", label: "Post-test End", type: "datetime"},
    ],
    fields: [
        {key: "course_id", label: "Course", type: "select", reference: "courses", required: true},
        {key: "week", label: "Week", type: "number", placeholder: "1"},
        {key: "topic_name", label: "Topic Name", required: true},
        {key: "show_topic", label: "Show Topic", type: "checkbox"},
        {key: "is_active", label: "Use for Student Dashboard", type: "checkbox", defaultValue: true, trueLabel: "Dashboard active", falseLabel: "Not active for dashboard"},
        {key: "pre_test_start_at", label: "Pre-test Start", type: "datetime-local"},
        {key: "pre_test_end_at", label: "Pre-test End", type: "datetime-local"},
        {key: "post_test_start_at", label: "Post-test Start", type: "datetime-local"},
        {key: "post_test_end_at", label: "Post-test End", type: "datetime-local"},
    ],
};

const STUDENT_INSTRUCTOR_ITEM = {
    label: "Student & Instructor",
    path: "/studentadmin",
    resource: "students",
    idKey: "user_id",
    canAdd: true,
    canDelete: false,
    description: "Assign students and instructors to courses, course groups, and roles.",
    tableColumns: [
        {key: "course_name", label: "Course"},
        {key: "name", label: "Name"},
        {key: "email", label: "Email"},
        {key: "role_name", label: "Role"},
        {key: "course_group_name", label: "Course Group"},
        {
            key: "virtual_space_enabled",
            label: "Virtual Space",
            type: "boolean",
            trueLabel: "Enabled",
            falseLabel: "Disabled",
        },
        {
            key: "gamification_enabled",
            label: "Gamification",
            type: "boolean",
            trueLabel: "Enabled",
            falseLabel: "Disabled",
        },
        {key: "created_at", label: "Created At", type: "datetime"},
    ],
    fields: [
        {key: "course_id", label: "Course", type: "select", reference: "courses", required: true},
        {key: "name", label: "User Name", required: true},
        {key: "email", label: "Email", required: true},
        {key: "role_id", label: "Role", type: "select", reference: "roles", valueKey: "role_id", labelKey: "role_name", required: true},
        {key: "course_group_id", label: "Course Group", type: "select", reference: "course_groups", dependsOn: "course_id", valueKey: "course_group_id", labelKey: "group_name", required: true},
    ],
};

const USER_ADMIN_ITEM = {
    label: "User Admin",
    path: "/useradmin",
    resource: "useradmins",
    idKey: "useradmin_id",
    canAdd: true,
    canDelete: true,
    description: "Manage accounts that can sign in to the GamifyIt admin backend.",
    tableColumns: [
        {key: "username", label: "Username"},
        {key: "role", label: "Admin Role"},
        {key: "user_name", label: "Linked User"},
        {key: "user_email", label: "Email"},
        {key: "is_disabled", label: "Status", type: "boolean", trueLabel: "Disabled", falseLabel: "Active"},
        {key: "last_login", label: "Last Login"},
    ],
    fields: [
        {key: "username", label: "Username", required: true},
        {key: "password", label: "Password", type: "password", requiredOnCreate: true, placeholder: "Leave blank to keep current password"},
        {
            key: "role",
            label: "Admin Role",
            type: "select",
            required: true,
            defaultValue: "instructor",
            options: [
                {value: "admin", label: "Admin"},
                {value: "instructor", label: "Instructor"},
            ],
        },
        {key: "user_id", label: "Linked App User", type: "select", reference: "useradmin_users", valueKey: "user_id", labelKey: "name"},
        {key: "is_disabled", label: "Disable Login", type: "checkbox", defaultValue: false, trueLabel: "Login disabled", falseLabel: "Login active"},
    ],
};

const USER_ACTIVITY_ITEM = {
    label: "User Activities",
    path: "/studentactivityadmin",
    custom: "userActivities",
    description: "View and clear tester activity records without deleting the user account.",
};

const MENU_GROUPS = [
    {
        label: "Admin Config",
        key: "admin-config",
        items: [
            {
                label: "Gamification Level",
                path: "/leveladmin",
                resource: "levels",
                idKey: "level_id",
                canAdd: false,
                canDelete: false,
                description: "Edit the XP range, level name, and color used for individual progress.",
                tableColumns: [
                    {key: "level_id", label: "Level"},
                    {key: "level_name", label: "Name"},
                    {key: "min_xp", label: "Min XP"},
                    {key: "max_xp", label: "Max XP"},
                    {key: "color_hex", label: "Color", type: "color"},
                ],
                fields: [
                    {key: "level_name", label: "Level Name", required: true},
                    {key: "min_xp", label: "Min XP", type: "number", required: true},
                    {key: "max_xp", label: "Max XP", type: "number", placeholder: "Empty for no maximum"},
                    {key: "color_hex", label: "Color", type: "color", required: true},
                ],
            },
            {
                label: "Avatar",
                path: "/avataradmin",
                resource: "avatars",
                idKey: "avatar_id",
                canAdd: true,
                canDelete: false,
                description: "Add or edit avatar display data used in the student login form.",
                tableColumns: [
                    {key: "avatar_name", label: "Avatar Name"},
                    {key: "avatar_public_path", label: "Public Path"},
                ],
                fields: [
                    {key: "avatar_name", label: "Avatar Name", required: true},
                    {key: "avatar_public_path", label: "Public Path", required: true, placeholder: "/student21/student21.png"},
                ],
            },
            {
                label: "Role",
                path: "/roleadmin",
                resource: "roles",
                idKey: "role_id",
                canAdd: false,
                canDelete: false,
                description: "Manage the student and instructor roles assigned to users.",
                tableColumns: [
                    {key: "role_id", label: "ID"},
                    {key: "role_name", label: "Role"},
                ],
                fields: [
                    {key: "role_name", label: "Role Name", required: true},
                ],
            },
            {
                label: "Settings",
                path: "/settingsadmin",
                resource: "settings",
                idKey: "setting_id",
                canAdd: false,
                canDelete: false,
                description: "Manage small system settings for access and behavior.",
                tableColumns: [
                    {key: "setting_name", label: "Setting"},
                    {key: "setting_description", label: "Description"},
                    {key: "setting_value", label: "Value", type: "setting_value"},
                ],
                fields: [
                    {key: "boolean_value", label: "Setting is active", type: "checkbox", trueLabel: "On", falseLabel: "Off", visibleForSettingTypes: ["boolean"]},
                    {key: "datetime_value", label: "Datetime", type: "datetime-local", visibleForSettingTypes: ["datetime"]},
                ],
            },
            COURSE_ITEM,
            TOPIC_ITEM,
            STUDENT_INSTRUCTOR_ITEM,
            USER_ACTIVITY_ITEM,
            USER_ADMIN_ITEM,
        ],
    },
    {
        label: "Course Master",
        key: "course-master",
        items: [
            {
                label: "Course Groups",
                path: "/coursegroupadmin",
                resource: "course-groups",
                idKey: "course_group_id",
                canAdd: true,
                canDelete: true,
                description: "Create course groups and control access mode plus gamification for each group.",
                tableColumns: [
                    {key: "course_name", label: "Course"},
                    {key: "group_name", label: "Group"},
                    {key: "virtual_space_enabled", label: "Virtual Space", type: "boolean", trueLabel: "Enabled", falseLabel: "Disabled"},
                    {key: "gamification_enabled", label: "Gamification", type: "boolean", trueLabel: "Enabled", falseLabel: "Disabled"},
                    {key: "student_count", label: "Students"},
                ],
                fields: [
                    {key: "course_id", label: "Course", type: "select", reference: "courses", required: true},
                    {key: "group_name", label: "Group Name", required: true},
                    {
                        key: "virtual_space_enabled",
                        label: "Enable Virtual Space",
                        type: "checkbox",
                        defaultValue: false,
                        trueLabel: "Students enter the virtual map",
                        falseLabel: "Students enter the no-map menu",
                    },
                    {
                        key: "gamification_enabled",
                        label: "Enable Gamification",
                        type: "checkbox",
                        defaultValue: false,
                        trueLabel: "XP and game layer enabled",
                        falseLabel: "XP and game layer disabled",
                    },
                ],
            },
            {
                label: "Material & Question Bank",
                path: "/questionbankadmin",
                custom: "questionBank",
                description: "Upload topic material, create a compact digest, generate AI question drafts, review them, and save to the selected bank.",
            },
        ],
    },
    {
        label: "Question Bank",
        key: "question-bank",
        items: [
            {
                label: "Copy Question Bank",
                path: "/copyquestionbankadmin",
                custom: "bankCopy",
            },
            {
                label: "Question Bank Monitor",
                path: "/questionbankmonitor",
                custom: "questionBankCoverage",
                description: "Monitor whether each topic has enough pre-test, post-test, individual, group case, and quiz questions.",
            },
            {
                label: "Quiz Question Bank",
                path: "/quizbankadmin",
                custom: "bankManager",
                bankType: "quiz_question_bank",
                description: "Display, add, and edit multiple choice questions for Kahoot-style quiz activity.",
            },
            {
                label: "Individual Question Bank",
                path: "/individualbankadmin",
                custom: "bankManager",
                bankType: "individual_questions",
                description: "Display, add, and edit individual exercise, pre-test, and post-test questions.",
            },
            {
                label: "Group Case Studies",
                path: "/groupcasebankadmin",
                custom: "bankManager",
                bankType: "topic_cases",
                description: "Display, add, and edit group case studies used by group activities.",
            },
        ],
    },
];

const DEFAULT_PATH = "/gamifyitadmin";
const BANK_PAGE_SIZE = 15;
const COPY_BANK_OPTIONS = [
    {value: "quiz", label: "Quiz - Multiple Choice", bankType: "quiz_question_bank", kind: "multiple_choice"},
    {value: "individual_mc", label: "Individual Exercise - Multiple Choice", bankType: "individual_questions", kind: "multiple_choice", activity: "exercise"},
    {value: "pre_test", label: "Pre-test - Multiple Choice", bankType: "individual_questions", kind: "multiple_choice", activity: "pre_test"},
    {value: "post_test", label: "Post-test - Multiple Choice", bankType: "individual_questions", kind: "multiple_choice", activity: "post_test"},
    {value: "individual_case", label: "Individual Case Study", bankType: "individual_questions", kind: "case_study", activity: "exercise"},
    {value: "group_case", label: "Group Case Study", bankType: "topic_cases", kind: "case_study"},
];
const OPENAI_MODEL_OPTIONS = [
    {value: "gpt-5.4-mini", label: "GPT-5.4 Mini - balanced"},
    {value: "gpt-5.4-nano", label: "GPT-5.4 Nano - fastest/cheapest"},
    {value: "gpt-5.4", label: "GPT-5.4 - higher quality"},
    {value: "gpt-5.5", label: "GPT-5.5 - best quality"},
];
const STANDALONE_ITEMS = [
    {
        label: "Change Password",
        path: "/adminpassword",
        custom: "changePassword",
        description: "Change the password for your current admin backend account.",
    },
];
const RESOURCE_ITEMS = [...MENU_GROUPS.flatMap((group) => group.items), ...STANDALONE_ITEMS];

function getConfig(pathname) {
    return RESOURCE_ITEMS.find((item) => item.path === pathname) || null;
}

function emptyForm(config) {
    if (!config) return {};
    return config.fields.reduce((acc, field) => {
        acc[field.key] = field.type === "checkbox" ? field.defaultValue ?? true : field.defaultValue ?? "";
        return acc;
    }, {});
}

function toDateTimeLocalValue(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const parts = new Intl.DateTimeFormat("en-CA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Asia/Jakarta",
    }).formatToParts(date).reduce((acc, part) => {
        acc[part.type] = part.value;
        return acc;
    }, {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

function buildForm(config, row) {
    if (!config || !row) return emptyForm(config);
    return config.fields.reduce((acc, field) => {
        const value = row[field.key];
        acc[field.key] = field.type === "checkbox"
            ? value !== false
            : field.type === "datetime-local"
                ? toDateTimeLocalValue(value)
                : value ?? "";
        return acc;
    }, {});
}

function formatValue(column, value) {
    if (column.type === "boolean") return value ? column.trueLabel || "Shown" : column.falseLabel || "Hidden";
    if (column.type === "setting_value") {
        if (value?.setting_type === "boolean") return value.boolean_value ? "On" : "Off";
        if (value?.setting_type === "datetime") return formatValue({type: "datetime"}, value.datetime_value);
        return value?.text_value || value?.number_value || "-";
    }
    if (column.type === "datetime") {
        if (!value) return "Belum dijadwalkan";
        const formatted = new Date(value).toLocaleString("id-ID", {
            dateStyle: "medium",
            timeStyle: "short",
            timeZone: "Asia/Jakarta",
        });
        return `${formatted} WIB`;
    }
    if (value === null || value === undefined || value === "") return "-";
    return value;
}

const BANK_CONFIGS = {
    quiz_question_bank: {
        title: "Quiz Question Bank",
        idKey: "question_id",
        questionType: "multiple_choice",
        columns: [
            {key: "course_name", label: "Course"},
            {key: "topic_name", label: "Topic"},
            {key: "question_number", label: "No."},
            {key: "question_text", label: "Question"},
            {key: "question_type", label: "Type"},
        ],
    },
    individual_questions: {
        title: "Individual Question Bank",
        idKey: "question_id",
        columns: [
            {key: "course_name", label: "Course"},
            {key: "topic_name", label: "Topic"},
            {key: "activity_type", label: "Activity"},
            {key: "question_kind", label: "Type"},
            {key: "question_number", label: "No."},
            {key: "question_text", label: "Question / Case"},
        ],
    },
    topic_cases: {
        title: "Group Case Studies",
        idKey: "case_id",
        questionType: "case_study",
        columns: [
            {key: "course_name", label: "Course"},
            {key: "topic_name", label: "Topic"},
            {key: "case_number", label: "Case No."},
            {key: "case_title", label: "Case Title"},
            {key: "question_type", label: "Type"},
        ],
    },
};

function getQuestionKindFromType(type) {
    return type === "exercise_case_study" ? "case_study" : "multiple_choice";
}

function getActivityFromType(type) {
    if (type === "pre_test_multiple_choice") return "pre_test";
    if (type === "post_test_multiple_choice") return "post_test";
    return "exercise";
}

function getIndividualTypeFromRow(row) {
    if (row?.activity_type === "pre_test") return "pre_test_multiple_choice";
    if (row?.activity_type === "post_test") return "post_test_multiple_choice";
    if (row?.question_kind === "case_study") return "exercise_case_study";
    return "exercise_multiple_choice";
}

function formatAdminNumber(value) {
    return new Intl.NumberFormat("en-US").format(Number(value || 0));
}

function formatAdminDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString(undefined, {month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"});
}

function scoreWidth(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.min(100, parsed));
}

const WEEKLY_BASELINE = {
    individual_mc: 2,
    individual_case: 1,
    group: 2,
    quiz: 4,
};

function emptyWeeklyCounts() {
    return {
        individual_mc: 0,
        individual_case: 0,
        group: 0,
        quiz: 0,
    };
}

function emptyWeeklySessionBuckets() {
    return Object.keys(WEEKLY_BASELINE).reduce((buckets, kind) => {
        buckets[kind] = new Set();
        return buckets;
    }, {});
}

function weeklyActivityKind(activity) {
    if (activity.type === "group") return "group";
    if (activity.type === "quiz") return "quiz";
    if (activity.type === "individual" && activity.activity_type === "exercise") {
        return activity.question_kind === "case_study" ? "individual_case" : "individual_mc";
    }
    return null;
}

function weeklyKindLabel(kind) {
    if (kind === "individual_mc") return "Individual MC";
    if (kind === "individual_case") return "Individual Case";
    if (kind === "group") return "Group Activity";
    if (kind === "quiz") return "Fun Quiz";
    return kind;
}

function weeklyActivitySessionKey(activity, topic, kind) {
    return [
        kind,
        activity.activity_id || activity.id || activity.session_id || activity.quiz_session_id || "activity",
        topic?.topic_id || "topic",
        activity.user_id || "",
        activity.submitted_at || "",
    ].join(":");
}

function createStudentBaseline(student) {
    return {
        user_id: student.user_id,
        name: student.name,
        email: student.email,
        counts: emptyWeeklyCounts(),
    };
}

function createProgressBucket(seedStudents, bucketData) {
    return {
        ...bucketData,
        topics: new Map(),
        totals: emptyWeeklyCounts(),
        session_keys: emptyWeeklySessionBuckets(),
        students: new Map(seedStudents.map((student) => [String(student.user_id), createStudentBaseline(student)])),
    };
}

function addActivityToProgressBucket(bucket, activity, topic, kind) {
    const sessionKey = weeklyActivitySessionKey(activity, topic, kind);
    if (!bucket.session_keys[kind].has(sessionKey)) {
        bucket.session_keys[kind].add(sessionKey);
        bucket.totals[kind] += 1;
    }
    bucket.topics.set(String(topic.topic_id), topic.topic_name);
    for (const participant of activity.participants || []) {
        if (!participant.user_id) continue;
        const studentKey = String(participant.user_id);
        if (!bucket.students.has(studentKey)) {
            bucket.students.set(studentKey, createStudentBaseline({
                user_id: participant.user_id,
                name: participant.name || activity.student_name || "Student",
                email: participant.email || "",
            }));
        }
        bucket.students.get(studentKey).counts[kind] += 1;
    }
}

function finalizeProgressBuckets(progressBuckets) {
    return progressBuckets
        .map((bucket) => {
            const students = [...bucket.students.values()].sort((a, b) => a.name.localeCompare(b.name));
            const withStatus = students.map((student) => ({
                ...student,
                baseline_met: Object.entries(WEEKLY_BASELINE).every(([kind, required]) => student.counts[kind] >= required),
            }));
            return {
                ...bucket,
                topic_names: [...bucket.topics.values()].sort((a, b) => a.localeCompare(b)),
                students_met: withStatus.filter((student) => student.baseline_met),
                students_pending: withStatus.filter((student) => !student.baseline_met),
            };
        });
}

function topicProgressForGroup(course, selectedGroup) {
    const topicBuckets = [];

    for (const topic of course?.topics || []) {
        const topicGroup = (topic.groups || []).find((group) => String(group.course_group_id) === String(selectedGroup.course_group_id));
        if (!topicGroup) continue;

        const topicBucket = createProgressBucket(selectedGroup.students || topicGroup.students || [], {
            key: `topic:${topic.topic_id}`,
            label: topic.topic_name,
            sub_label: topic.week ? `Week ${topic.week}` : "Topic",
            start_time: Number(topic.week || 0),
        });

        for (const activity of topicGroup.activities || []) {
            const kind = weeklyActivityKind(activity);
            if (!kind || !activity.submitted_at) continue;
            addActivityToProgressBucket(topicBucket, activity, topic, kind);
        }
        if (Object.values(topicBucket.totals).some((total) => total > 0)) {
            topicBuckets.push(topicBucket);
        }
    }

    return finalizeProgressBuckets(topicBuckets)
        .sort((a, b) => a.start_time - b.start_time || a.label.localeCompare(b.label));
}

function missingAssessmentStudents(group) {
    const assessmentByUserId = new Map((group.assessment_comparison || []).map((student) => [String(student.user_id), student]));
    const students = [...(group.students || [])].sort((a, b) => a.name.localeCompare(b.name));
    return {
        preTest: students.filter((student) => {
            const assessment = assessmentByUserId.get(String(student.user_id));
            return assessment?.pre_score === null || assessment?.pre_score === undefined;
        }),
        postTest: students.filter((student) => {
            const assessment = assessmentByUserId.get(String(student.user_id));
            return assessment?.post_score === null || assessment?.post_score === undefined;
        }),
    };
}

function escapeXml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function slugifyFilePart(value) {
    return String(value || "topic")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "topic";
}

function percentage(value, total) {
    if (!total) return 0;
    return Math.round((Number(value || 0) / Number(total || 0)) * 100);
}

function barWidth(value, max) {
    if (!max || !Number(value || 0)) return 0;
    return Math.max(4, Math.min(100, Math.round((Number(value || 0) / Number(max || 0)) * 100)));
}

function isIndividualCaseActivity(activity) {
    return /case/i.test(`${activity.label || ""} ${activity.title || ""}`);
}

function matchesTopicScope(item, topicId) {
    return !topicId || !item.topic_id || String(item.topic_id) === String(topicId);
}

function groupActivityComparison(group, topicId = "") {
    const activities = (group.activities || []).filter((activity) => matchesTopicScope(activity, topicId));
    return {
        individual_mc: activities.filter((activity) => activity.type === "individual" && !isIndividualCaseActivity(activity)).length,
        individual_case: activities.filter((activity) => activity.type === "individual" && isIndividualCaseActivity(activity)).length,
        group: activities.filter((activity) => activity.type === "group").length,
        quiz: activities.filter((activity) => activity.type === "quiz").length,
    };
}

function topicComparisonRows(groups = [], topicId = "") {
    const rows = groups.map((group) => {
        const activityCounts = groupActivityComparison(group, topicId);
        const scopedActivities = (group.activities || []).filter((activity) => matchesTopicScope(activity, topicId));
        const scopedContributions = (group.xp_contributions || []).filter((item) => matchesTopicScope(item, topicId));
        const individualXp = scopedContributions
            .filter((item) => item.activity_type !== "group")
            .reduce((total, item) => total + Number(item.xp_earned || 0), 0);
        const sharedGroupXp = scopedActivities
            .filter((activity) => activity.type === "group")
            .reduce((total, activity) => total + Number(activity.xp || 0), 0);
        const studentsEarnedGroupXp = scopedContributions
            .filter((item) => item.activity_type === "group")
            .reduce((total, item) => total + Number(item.xp_earned || 0), 0);
        return {
            group,
            groupName: group.group_name || "Ungrouped",
            individualXp,
            sharedGroupXp,
            studentsEarnedGroupXp,
            totalActivities: Object.values(activityCounts).reduce((total, value) => total + value, 0),
            activityCounts,
        };
    });
    const maxActivities = Math.max(0, ...rows.map((row) => row.totalActivities));
    const totals = {
        individualXp: rows.reduce((total, row) => total + row.individualXp, 0),
        sharedGroupXp: rows.reduce((total, row) => total + row.sharedGroupXp, 0),
        studentsEarnedGroupXp: rows.reduce((total, row) => total + row.studentsEarnedGroupXp, 0),
    };
    const highest = {
        individualXp: Math.max(0, ...rows.map((row) => row.individualXp)),
        sharedGroupXp: Math.max(0, ...rows.map((row) => row.sharedGroupXp)),
        studentsEarnedGroupXp: Math.max(0, ...rows.map((row) => row.studentsEarnedGroupXp)),
    };
    return rows.map((row) => ({
        ...row,
        maxActivities,
        xpTypePercentages: {
            individualXp: percentage(row.individualXp, totals.individualXp),
            sharedGroupXp: percentage(row.sharedGroupXp, totals.sharedGroupXp),
            studentsEarnedGroupXp: percentage(row.studentsEarnedGroupXp, totals.studentsEarnedGroupXp),
        },
        topXpTypes: {
            individualXp: highest.individualXp > 0 && row.individualXp === highest.individualXp,
            sharedGroupXp: highest.sharedGroupXp > 0 && row.sharedGroupXp === highest.sharedGroupXp,
            studentsEarnedGroupXp: highest.studentsEarnedGroupXp > 0 && row.studentsEarnedGroupXp === highest.studentsEarnedGroupXp,
        },
    }));
}

function levelRuleForXp(levelRules = [], xp = 0) {
    const parsedXp = Number(xp || 0);
    return (levelRules || []).find((level) => (
        parsedXp >= Number(level.min_xp || 0)
        && (level.max_xp === null || level.max_xp === undefined || parsedXp <= Number(level.max_xp))
    )) || levelRules?.[0] || {
        level_id: "unleveled",
        level_name: "No level",
        color_hex: "#64748b",
        min_xp: 0,
        max_xp: null,
    };
}

function topicLevelDistribution(group, topicId = "") {
    const levelRules = (group.level_rules?.length ? group.level_rules : group.level_distribution || [])
        .map((level) => ({
            level_id: level.level_id,
            level_name: level.level_name,
            color_hex: level.color_hex || level.level_color || "#64748b",
            min_xp: level.min_xp ?? 0,
            max_xp: level.max_xp ?? null,
        }));
    const xpByStudent = new Map((group.students || []).map((student) => [String(student.user_id), 0]));

    for (const contribution of group.xp_contributions || []) {
        if (!matchesTopicScope(contribution, topicId)) continue;
        const key = String(contribution.user_id);
        xpByStudent.set(key, Number(xpByStudent.get(key) || 0) + Number(contribution.xp_earned || 0));
    }

    const byLevel = new Map();
    for (const student of group.students || []) {
        const topicXp = Number(xpByStudent.get(String(student.user_id)) || 0);
        const level = levelRuleForXp(levelRules, topicXp);
        const levelKey = String(level.level_id);
        const current = byLevel.get(levelKey) || {
            ...level,
            students: [],
        };
        current.students.push({...student, topic_xp: topicXp});
        byLevel.set(levelKey, current);
    }

    return [...byLevel.values()]
        .map((level) => ({
            ...level,
            students: level.students.sort((a, b) => b.topic_xp - a.topic_xp || a.name.localeCompare(b.name)),
        }))
        .filter((level) => level.students.length > 0)
        .sort((a, b) => Number(a.min_xp || 0) - Number(b.min_xp || 0));
}

function columnName(index) {
    let name = "";
    let current = index + 1;
    while (current > 0) {
        const remainder = (current - 1) % 26;
        name = String.fromCharCode(65 + remainder) + name;
        current = Math.floor((current - 1) / 26);
    }
    return name;
}

const crcTable = (() => {
    const table = [];
    for (let n = 0; n < 256; n += 1) {
        let c = n;
        for (let k = 0; k < 8; k += 1) {
            c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
        }
        table[n] = c >>> 0;
    }
    return table;
})();

function crc32(bytes) {
    let crc = 0xffffffff;
    for (const byte of bytes) {
        crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
    }
    return (crc ^ 0xffffffff) >>> 0;
}

function writeUint16(bytes, value) {
    bytes.push(value & 0xff, (value >>> 8) & 0xff);
}

function writeUint32(bytes, value) {
    bytes.push(value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff);
}

function concatBytes(parts) {
    const totalLength = parts.reduce((total, part) => total + part.length, 0);
    const output = new Uint8Array(totalLength);
    let offset = 0;
    for (const part of parts) {
        output.set(part, offset);
        offset += part.length;
    }
    return output;
}

function createStoredZip(entries) {
    const encoder = new TextEncoder();
    const localParts = [];
    const centralParts = [];
    let offset = 0;

    for (const entry of entries) {
        const nameBytes = encoder.encode(entry.name);
        const dataBytes = encoder.encode(entry.content);
        const crc = crc32(dataBytes);
        const local = [];
        writeUint32(local, 0x04034b50);
        writeUint16(local, 20);
        writeUint16(local, 0);
        writeUint16(local, 0);
        writeUint16(local, 0);
        writeUint16(local, 0);
        writeUint32(local, crc);
        writeUint32(local, dataBytes.length);
        writeUint32(local, dataBytes.length);
        writeUint16(local, nameBytes.length);
        writeUint16(local, 0);
        const localBytes = concatBytes([new Uint8Array(local), nameBytes, dataBytes]);
        localParts.push(localBytes);

        const central = [];
        writeUint32(central, 0x02014b50);
        writeUint16(central, 20);
        writeUint16(central, 20);
        writeUint16(central, 0);
        writeUint16(central, 0);
        writeUint16(central, 0);
        writeUint16(central, 0);
        writeUint32(central, crc);
        writeUint32(central, dataBytes.length);
        writeUint32(central, dataBytes.length);
        writeUint16(central, nameBytes.length);
        writeUint16(central, 0);
        writeUint16(central, 0);
        writeUint16(central, 0);
        writeUint16(central, 0);
        writeUint32(central, 0);
        writeUint32(central, offset);
        centralParts.push(concatBytes([new Uint8Array(central), nameBytes]));
        offset += localBytes.length;
    }

    const centralDirectory = concatBytes(centralParts);
    const end = [];
    writeUint32(end, 0x06054b50);
    writeUint16(end, 0);
    writeUint16(end, 0);
    writeUint16(end, entries.length);
    writeUint16(end, entries.length);
    writeUint32(end, centralDirectory.length);
    writeUint32(end, offset);
    writeUint16(end, 0);

    return concatBytes([...localParts, centralDirectory, new Uint8Array(end)]);
}

function workbookCellValue(cell) {
    if (cell && typeof cell === "object" && !Array.isArray(cell)) return cell.value ?? "";
    return cell;
}

function workbookCellStyle(cell) {
    if (!cell || typeof cell !== "object" || Array.isArray(cell)) return "";
    if (cell.style === "danger") return " s=\"1\"";
    if (cell.style === "header") return " s=\"2\"";
    return "";
}

function worksheetCell(rowIndex, colIndex, cell) {
    const ref = `${columnName(colIndex)}${rowIndex + 1}`;
    const value = workbookCellValue(cell);
    const style = workbookCellStyle(cell);
    if (typeof value === "number" && Number.isFinite(value)) {
        return `<c r="${ref}"${style}><v>${value}</v></c>`;
    }
    return `<c r="${ref}"${style} t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
}

function createXlsxBlob(rows, sheetName = "Export", additionalSheets = []) {
    const sheets = [{name: sheetName, rows}, ...additionalSheets];
    const alignment = additionalSheets.length ? '<alignment vertical="top" wrapText="1"/>' : "";
    const entries = [
        {
            name: "[Content_Types].xml",
            content: `<?xml version="1.0" encoding="UTF-8"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${sheets.map((sheet, index) => `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join("\n")}
</Types>`,
        },
        {
            name: "_rels/.rels",
            content: `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
        },
        {
            name: "xl/workbook.xml",
            content: `<?xml version="1.0" encoding="UTF-8"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${sheets.map((sheet, index) => `<sheet name="${escapeXml(sheet.name.slice(0, 31))}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`).join("")}</sheets>
</workbook>`,
        },
        {
            name: "xl/_rels/workbook.xml.rels",
            content: `<?xml version="1.0" encoding="UTF-8"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${sheets.map((sheet, index) => `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`).join("\n")}
<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
        },
        {
            name: "xl/styles.xml",
            content: `<?xml version="1.0" encoding="UTF-8"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<fonts count="3">
<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="FF991B1B"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/><family val="2"/></font>
</fonts>
<fills count="4">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFFEE2E2"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FF0F766E"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="3">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0" applyAlignment="1">${alignment}</xf>
<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1">${alignment}</xf>
<xf numFmtId="0" fontId="2" fillId="3" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1">${alignment}</xf>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`,
        },
        ...sheets.map((sheet, index) => {
            const headerIndex = sheet.rows.findIndex((row) => row.some((cell) => cell?.style === "header"));
            const widths = [];
            for (const row of sheet.rows.slice(Math.max(0, headerIndex))) row.forEach((cell, colIndex) => {
                widths[colIndex] = Math.min(85, Math.max(widths[colIndex] || 14, String(workbookCellValue(cell) ?? "").length + 2));
            });
            const sheetRows = sheet.rows.map((row, rowIndex) => {
                const lines = Math.max(1, ...row.map((cell, colIndex) => (
                    String(workbookCellValue(cell) ?? "").split("\n").reduce((total, line) => (
                        total + Math.max(1, Math.ceil(line.length / Math.max(1, (widths[colIndex] || 14) - 3)))
                    ), 0)
                )));
                const height = additionalSheets.length ? ` ht="${Math.min(409, lines * 16 + 8)}" customHeight="1"` : "";
                return `<row r="${rowIndex + 1}"${height}>${row.map((cell, colIndex) => worksheetCell(rowIndex, colIndex, cell)).join("")}</row>`;
            }).join("");
            return {
            name: `xl/worksheets/sheet${index + 1}.xml`,
            content: `<?xml version="1.0" encoding="UTF-8"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
${additionalSheets.length ? `<sheetViews><sheetView workbookViewId="0"><pane ySplit="${headerIndex + 1}" topLeftCell="A${headerIndex + 2}" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
<cols>${widths.map((width, colIndex) => `<col min="${colIndex + 1}" max="${colIndex + 1}" width="${width}" customWidth="1"/>`).join("")}</cols>` : ""}
<sheetData>${sheetRows}</sheetData>
</worksheet>`,
            };
        }),
    ];

    return new Blob([createStoredZip(entries)], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
}

const QUESTION_BANK_TARGETS = [
    {
        value: "quiz_question_bank",
        label: "Quiz Question Bank",
        bank_type: "quiz_question_bank",
        count: 5,
    },
    {
        value: "exercise_multiple_choice",
        label: "Individual Exercise Question Bank",
        bank_type: "individual_questions",
        individual_question_type: "exercise_multiple_choice",
        count: 5,
    },
    {
        value: "pre_test_multiple_choice",
        label: "Pre-test Question Bank",
        bank_type: "individual_questions",
        individual_question_type: "pre_test_multiple_choice",
        count: 20,
    },
    {
        value: "post_test_multiple_choice",
        label: "Post-test Question Bank",
        bank_type: "individual_questions",
        individual_question_type: "post_test_multiple_choice",
        count: 20,
    },
    {
        value: "exercise_case_study",
        label: "Individual Case Study Bank",
        bank_type: "individual_questions",
        individual_question_type: "exercise_case_study",
        count: 15,
    },
    {
        value: "topic_cases",
        label: "Group Case Studies",
        bank_type: "topic_cases",
        count: 15,
    },
];

function getQuestionBankTarget(settings) {
    if (settings.bank_type === "individual_questions") {
        return settings.individual_question_type || "exercise_multiple_choice";
    }
    return settings.bank_type || "quiz_question_bank";
}

const AdminPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const activeConfig = useMemo(() => getConfig(location.pathname), [location.pathname]);
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [topicSummaryDownloading, setTopicSummaryDownloading] = useState(false);
    const [message, setMessage] = useState("");
    const [rows, setRows] = useState([]);
    const [rowsResource, setRowsResource] = useState("");
    const [references, setReferences] = useState({instructors: [], courses: [], course_groups: [], roles: [], useradmin_users: [], students: []});
    const [editingRow, setEditingRow] = useState(null);
    const [formData, setFormData] = useState({});
    const [openMenus, setOpenMenus] = useState({
        "admin-config": true,
        "course-master": true,
        "question-bank": true,
    });
    const [loginForm, setLoginForm] = useState({
        username: "",
        password: "",
    });
    const [passwordForm, setPasswordForm] = useState({
        current_password: "",
        new_password: "",
        confirm_password: "",
    });
    const [materials, setMaterials] = useState([]);
    const [materialTopicFilter, setMaterialTopicFilter] = useState(() => readSessionValue(QUESTION_BANK_TOPIC_SESSION_KEY));
    const [materialForm, setMaterialForm] = useState({
        topic_id: "",
        title: "",
        content_text: "",
    });
    const [editingMaterial, setEditingMaterial] = useState(null);
    const [questionSettings, setQuestionSettings] = useState({
        topic_id: readSessionValue(QUESTION_BANK_TOPIC_SESSION_KEY),
        material_ids: [],
        bank_type: "quiz_question_bank",
        activity_type: "exercise",
        question_kind: "multiple_choice",
        individual_question_type: "exercise_multiple_choice",
        openai_model: "gpt-5.4-mini",
        custom_instruction: "",
        count: 5,
    });
    const [instructionPresets, setInstructionPresets] = useState([]);
    const [editingInstructionPresetId, setEditingInstructionPresetId] = useState(null);
    const [instructionPresetForm, setInstructionPresetForm] = useState({
        preset_name: "",
        instruction_text: "",
    });
    const [drafts, setDrafts] = useState([]);
    const [draftMeta, setDraftMeta] = useState(null);
    const [bankRows, setBankRows] = useState([]);
    const [bankPage, setBankPage] = useState(1);
    const [bankCopy, setBankCopy] = useState({source_course_id: "", source_topic_id: "", source_bank: "quiz", target_course_id: "", target_topic_id: "", target_bank: "individual_mc"});
    const [bankCopyRows, setBankCopyRows] = useState([]);
    const [bankCopyIds, setBankCopyIds] = useState([]);
    const [bankCopyPage, setBankCopyPage] = useState(1);
    const [bankCopyLoading, setBankCopyLoading] = useState(false);
    const [bankCopySaving, setBankCopySaving] = useState(false);
    const [bankCopyError, setBankCopyError] = useState("");
    const [bankForm, setBankForm] = useState({});
    const [editingBankRow, setEditingBankRow] = useState(null);
    const [bankFilters, setBankFilters] = useState({course_id: "", topic_id: "", activity_type: "", question_kind: ""});
    const [questionBankCoverageRows, setQuestionBankCoverageRows] = useState([]);
    const [questionBankCoverageCourseId, setQuestionBankCoverageCourseId] = useState(() => readSessionValue(QUESTION_BANK_COVERAGE_COURSE_SESSION_KEY));
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [selectedBankIds, setSelectedBankIds] = useState([]);
    const [topicCourseFilter, setTopicCourseFilter] = useState(() => readSessionValue(TOPIC_ADMIN_COURSE_SESSION_KEY));
    const [topicReset, setTopicReset] = useState({
        topic_id: "",
        course_id: "",
        course_group_id: "",
        search: "",
        activity_types: ["pre_test"],
        user_ids: [],
    });
    const [userActivityUserId, setUserActivityUserId] = useState(() => readSessionValue(USER_ACTIVITY_USER_SESSION_KEY));
    const [userActivityData, setUserActivityData] = useState(null);
    const [userActivityPage, setUserActivityPage] = useState(1);
    const [selectedUserActivities, setSelectedUserActivities] = useState([]);
    const [studentBulk, setStudentBulk] = useState({
        course_id: "",
        course_group_id: "",
        search: "",
        target_course_group_id: "",
    });
    const [instructorDashboard, setInstructorDashboard] = useState(null);
    const [selectedDashboardCourseId, setSelectedDashboardCourseId] = useState(() => readDashboardCourseSession());
    const [selectedDashboardTopicId, setSelectedDashboardTopicId] = useState("");

    const displayedRows = useMemo(() => {
        if (activeConfig?.resource && rowsResource !== activeConfig.resource) return [];
        if (activeConfig?.resource === "topics") {
            return rows.filter((row) => (
                !topicCourseFilter || String(row.course_id) === String(topicCourseFilter)
            ));
        }
        if (activeConfig?.resource !== "students") return rows;
        const search = studentBulk.search.trim().toLowerCase();
        return rows.filter((row) => {
            if (studentBulk.course_id && String(row.course_id) !== String(studentBulk.course_id)) return false;
            if (studentBulk.course_group_id && String(row.course_group_id) !== String(studentBulk.course_group_id)) return false;
            if (!search) return true;
            return [row.name, row.email, row.course_name, row.course_group_name, row.role_name]
                .some((value) => String(value || "").toLowerCase().includes(search));
        });
    }, [activeConfig, rows, rowsResource, studentBulk, topicCourseFilter]);
    const topicResetStudents = useMemo(() => {
        const search = topicReset.search.trim().toLowerCase();
        return (references.students || []).filter((student) => {
            if (topicReset.course_id && String(student.course_id) !== String(topicReset.course_id)) return false;
            if (topicReset.course_group_id && String(student.course_group_id) !== String(topicReset.course_group_id)) return false;
            if (!search) return true;
            return [student.name, student.email, student.course_name, student.course_group_name]
                .some((value) => String(value || "").toLowerCase().includes(search));
        });
    }, [references.students, topicReset]);
    const visibleMenuGroups = useMemo(() => {
        const isAdmin = String(admin?.role || "").toLowerCase() === "admin";
        return MENU_GROUPS.filter((group) => isAdmin || group.key !== "admin-config");
    }, [admin?.role]);
    const dashboardCourses = useMemo(() => instructorDashboard?.courses || [], [instructorDashboard]);
    const selectedDashboardCourse = useMemo(() => {
        if (!dashboardCourses.length) return null;
        return dashboardCourses.find((course) => String(course.course_id) === String(selectedDashboardCourseId)) || dashboardCourses[0];
    }, [dashboardCourses, selectedDashboardCourseId]);
    const selectedDashboardTopic = useMemo(() => {
        const topics = selectedDashboardCourse?.topics || [];
        if (!topics.length) return null;
        return topics.find((topic) => String(topic.topic_id) === String(selectedDashboardTopicId)) || topics[0];
    }, [selectedDashboardCourse, selectedDashboardTopicId]);

    useEffect(() => {
        if (!dashboardCourses.length) return;
        const selectedExists = dashboardCourses.some((course) => String(course.course_id) === String(selectedDashboardCourseId));
        if (selectedExists) {
            writeDashboardCourseSession(selectedDashboardCourseId);
            return;
        }

        const storedCourseId = readDashboardCourseSession();
        const storedCourse = dashboardCourses.find((course) => String(course.course_id) === String(storedCourseId));
        const nextCourseId = storedCourse?.course_id || dashboardCourses[0]?.course_id || "";
        if (nextCourseId) {
            setSelectedDashboardCourseId(nextCourseId);
            writeDashboardCourseSession(nextCourseId);
        }
    }, [dashboardCourses, selectedDashboardCourseId]);

    useEffect(() => {
        let active = true;
        apiGet("/admin/session")
            .then((data) => {
                if (!active) return;
                if (data.loggedIn) setAdmin(data.admin);
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    useEffect(() => {
        if (!admin) return;
        apiGet("/admin/references").then((data) => {
            if (!data.message) setReferences(data);
        });
    }, [admin]);

    useEffect(() => {
        if (activeConfig?.resource !== "topics") return;
        const courses = references.courses || [];
        if (!courses.length) return;
        const selectedExists = courses.some((course) => String(course.course_id) === String(topicCourseFilter));
        if (!selectedExists) {
            const firstCourseId = String(courses[0].course_id || "");
            setTopicCourseFilter(firstCourseId);
            writeSessionValue(TOPIC_ADMIN_COURSE_SESSION_KEY, firstCourseId);
        }
    }, [activeConfig, references.courses, topicCourseFilter]);

    useEffect(() => {
        if (activeConfig?.custom !== "questionBankCoverage") return;
        const courses = references.courses || [];
        if (!courses.length) return;
        const selectedExists = courses.some((course) => String(course.course_id) === String(questionBankCoverageCourseId));
        if (!selectedExists) {
            const firstCourseId = String(courses[0].course_id || "");
            setQuestionBankCoverageCourseId(firstCourseId);
            writeSessionValue(QUESTION_BANK_COVERAGE_COURSE_SESSION_KEY, firstCourseId);
        }
    }, [activeConfig, references.courses, questionBankCoverageCourseId]);

    useEffect(() => {
        if (!admin || activeConfig) return undefined;
        let active = true;
        setBusy(true);
        apiGet("/instructor/dashboard")
            .then((data) => {
                if (!active) return;
                if (data.message && !data.courses) {
                    setMessage(data.message);
                    setInstructorDashboard(null);
                    return;
                }
                setInstructorDashboard(data);
                const firstCourse = data.courses?.[0];
                setSelectedDashboardCourseId((current) => current || firstCourse?.course_id || "");
                setSelectedDashboardTopicId((current) => current || firstCourse?.topics?.[0]?.topic_id || "");
            })
            .finally(() => {
                if (active) setBusy(false);
            });
        return () => {
            active = false;
        };
    }, [admin, activeConfig]);

    useEffect(() => {
        if (!selectedDashboardCourse) return;
        const topicExists = selectedDashboardCourse.topics?.some((topic) => String(topic.topic_id) === String(selectedDashboardTopicId));
        if (!topicExists) {
            setSelectedDashboardTopicId(selectedDashboardCourse.topics?.[0]?.topic_id || "");
        }
    }, [selectedDashboardCourse, selectedDashboardTopicId]);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 650);
        };
        handleScroll();
        window.addEventListener("scroll", handleScroll, {passive: true});
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        let active = true;
        const resource = activeConfig?.resource;
        const custom = activeConfig?.custom;
        const bankType = activeConfig?.bankType;

        setEditingRow(null);
        setFormData({});
        setEditingBankRow(null);
        setBankForm({});
        setBankPage(1);
        setBankFilters(activeConfig?.custom === "bankManager"
            ? readBankFiltersSession(activeConfig.bankType)
            : {course_id: "", topic_id: "", activity_type: "", question_kind: ""});
        setSelectedStudentIds([]);
        setSelectedBankIds([]);
        setStudentBulk({course_id: "", course_group_id: "", search: "", target_course_group_id: ""});
        if (activeConfig?.resource !== "topics") {
            setTopicCourseFilter(readSessionValue(TOPIC_ADMIN_COURSE_SESSION_KEY));
        }
        setTopicReset({topic_id: "", course_id: "", course_group_id: "", search: "", activity_types: ["pre_test"], user_ids: []});
        setMessage("");
        setRows([]);
        setRowsResource(resource || "");
        if (custom !== "bankManager") {
            setBankRows([]);
        }
        if (custom !== "questionBankCoverage") {
            setQuestionBankCoverageRows([]);
        }
        if (custom !== "userActivities") {
            setUserActivityData(null);
        }
        if (admin && resource) {
            setBusy(true);
            apiGet(`/admin/resources/${resource}`)
                .then((data) => {
                    if (!active) return;
                    setRows(data.rows || []);
                    setRowsResource(resource);
                    if (data.message) setMessage(data.message);
                })
                .finally(() => {
                    if (active) setBusy(false);
                });
        }
        if (admin && custom === "questionBank") {
            loadMaterials();
            loadInstructionPresets();
        }
        if (admin && custom === "bankManager") {
            setBusy(true);
            apiGet(`/admin/question-bank/${bankType}`)
                .then((data) => {
                    if (!active) return;
                    setBankRows(data.rows || []);
                    setBankPage(1);
                    if (data.message) setMessage(data.message);
                })
                .finally(() => {
                    if (active) setBusy(false);
                });
        }
        return () => {
            active = false;
        };
    }, [admin, activeConfig]);

    useEffect(() => {
        if (!admin || activeConfig?.custom !== "questionBankCoverage" || !questionBankCoverageCourseId) return;
        let active = true;
        setBusy(true);
        setQuestionBankCoverageRows([]);
        apiGet(`/admin/question-bank-coverage?course_id=${encodeURIComponent(questionBankCoverageCourseId)}`)
            .then((data) => {
                if (!active) return;
                setQuestionBankCoverageRows(data.rows || []);
                if (data.message) setMessage(data.message);
            })
            .finally(() => {
                if (active) setBusy(false);
            });
        return () => {
            active = false;
        };
    }, [admin, activeConfig, questionBankCoverageCourseId]);

    useEffect(() => {
        if (!admin || activeConfig?.custom !== "bankCopy") return;
        let active = true;
        setBankCopyRows([]);
        setBankCopyIds([]);
        setBankCopyPage(1);
        setBankCopyError("");
        if (!bankCopy.source_topic_id) {
            setBankCopyLoading(false);
            return;
        }
        const source = COPY_BANK_OPTIONS.find((option) => option.value === bankCopy.source_bank);
        setBankCopyLoading(true);
        apiGet(`/admin/question-bank/${source.bankType}?topic_id=${encodeURIComponent(bankCopy.source_topic_id)}`)
            .then((data) => {
                if (!active) return;
                if (!Array.isArray(data.rows)) {
                    setBankCopyError(data.message || "Gagal memuat daftar soal.");
                    return;
                }
                setBankCopyRows(data.rows.filter((row) => !source.activity || (row.activity_type === source.activity && row.question_kind === source.kind)));
            })
            .catch(() => {
                if (active) setBankCopyError("Gagal memuat daftar soal. Periksa koneksi dan coba lagi.");
            })
            .finally(() => {
                if (active) setBankCopyLoading(false);
            });
        return () => { active = false; };
    }, [admin, activeConfig, bankCopy.source_topic_id, bankCopy.source_bank]);

    const loadRows = async (config = activeConfig) => {
        if (!config) return;
        setBusy(true);
        const data = await apiGet(`/admin/resources/${config.resource}`);
        setRows(data.rows || []);
        setRowsResource(config.resource);
        if (data.message) setMessage(data.message);
        setBusy(false);
    };

    const refreshReferences = async () => {
        const data = await apiGet("/admin/references");
        if (!data.message) setReferences(data);
    };

    const loadMaterials = async () => {
        try {
            const data = await apiGet("/admin/materials");
            if (!Array.isArray(data.materials)) {
                setMessage(data.message || "Gagal mengambil course material. Silakan muat ulang halaman.");
                return;
            }
            setMaterials(data.materials);
        } catch (error) {
            setMessage("Gagal mengambil course material. Periksa koneksi dan muat ulang halaman.");
        }
    };

    const loadInstructionPresets = async () => {
        try {
            const data = await apiGet("/admin/question-instruction-presets");
            if (!Array.isArray(data.presets)) {
                setMessage(data.message || "Gagal mengambil preset instruction. Silakan muat ulang halaman.");
                return;
            }
            setInstructionPresets(data.presets);
        } catch (error) {
            setMessage("Gagal mengambil preset instruction. Periksa koneksi dan muat ulang halaman.");
        }
    };

    const loadBankRows = async (bankType = activeConfig?.bankType) => {
        if (!bankType) return;
        setBusy(true);
        const data = await apiGet(`/admin/question-bank/${bankType}`);
        setBankRows(data.rows || []);
        setBankPage(1);
        setSelectedBankIds([]);
        if (data.message) setMessage(data.message);
        setBusy(false);
    };

    const loadQuestionBankCoverage = async (courseId = questionBankCoverageCourseId) => {
        if (!courseId) {
            setQuestionBankCoverageRows([]);
            return;
        }
        setBusy(true);
        setQuestionBankCoverageRows([]);
        const data = await apiGet(`/admin/question-bank-coverage?course_id=${encodeURIComponent(courseId)}`);
        setQuestionBankCoverageRows(data.rows || []);
        if (data.message) setMessage(data.message);
        setBusy(false);
    };

    const loadUserActivity = async (userId = userActivityUserId, options = {}) => {
        if (!userId) {
            setUserActivityData(null);
            return;
        }
        setBusy(true);
        if (!options.quiet) setMessage("");
        try {
            const data = await apiGet(`/admin/users/${encodeURIComponent(userId)}/activities?page=${userActivityPage}`);
            if (data.user) {
                setUserActivityData(data);
                setUserActivityPage(data.pagination?.page || 1);
                setSelectedUserActivities([]);
            } else {
                setUserActivityData(null);
                if (data.message) setMessage(data.message);
            }
        } catch (error) {
            setMessage("Gagal memuat aktivitas. Periksa koneksi dan coba lagi.");
        } finally {
            setBusy(false);
        }
    };

    const updateUserActivityUser = async (userId) => {
        setUserActivityData(null);
        setUserActivityPage(1);
        setSelectedUserActivities([]);
        setUserActivityUserId(userId);
        writeSessionValue(USER_ACTIVITY_USER_SESSION_KEY, userId);
    };

    const deleteSelectedActivityRows = async () => {
        if (busy || !selectedUserActivities.length || String(userActivityData?.user?.user_id) !== String(userActivityUserId)) return;
        const shared = selectedUserActivities.some((activity) => activity.type === "group" || activity.type === "quiz");
        const warning = shared ? "\nPERHATIAN: sesi Group Activity/Fun Quiz yang dipilih dihapus untuk SEMUA peserta, termasuk jawaban, hasil, dan skor mereka." : "";
        if (!window.confirm(`Hapus permanen ${selectedUserActivities.length} aktivitas yang dipilih beserta jawaban dan skor terkait? Aktivitas lain dan akun user tetap ada.${warning}`)) return;
        setBusy(true);
        setMessage("");
        try {
            const data = await apiPost(`/admin/users/${encodeURIComponent(userActivityUserId)}/activities/delete-selected`, {activities: selectedUserActivities});
            if (data.message) setMessage(data.message);
            if (data.data) await loadUserActivity(userActivityUserId, {quiet: true});
        } catch (error) {
            setMessage("Koneksi terputus. Muat ulang daftar untuk memastikan status penghapusan.");
        } finally {
            setBusy(false);
        }
    };

    const deleteSelectedUserActivity = async () => {
        if (!userActivityUserId || !userActivityData?.user) {
            setMessage("Pilih user terlebih dahulu.");
            return;
        }
        const label = userActivityData.user.name || userActivityData.user.email || "user ini";
        if (!window.confirm(`Hapus semua aktivitas ${label}? User account tetap ada, tetapi session, log, progress, quiz, individual, group activity, dan XP history akan dihapus. Sesi group dan quiz terkait juga dihapus untuk SEMUA peserta, termasuk skor mereka.`)) return;

        setBusy(true);
        setMessage("");
        try {
            const data = await apiDelete(`/admin/users/${encodeURIComponent(userActivityUserId)}/activities`);
            if (data.message) setMessage(data.message);
            if (data.data) await loadUserActivity(userActivityUserId, {quiet: true});
        } catch (error) {
            setMessage("Koneksi terputus. Muat ulang daftar untuk memastikan status penghapusan.");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        if (!admin || activeConfig?.custom !== "userActivities") return undefined;
        if (!userActivityUserId) {
            setUserActivityData(null);
            return undefined;
        }

        let active = true;
        setBusy(true);
        apiGet(`/admin/users/${encodeURIComponent(userActivityUserId)}/activities?page=${userActivityPage}`)
            .then((data) => {
                if (!active) return;
                if (data.user) {
                    setUserActivityData(data);
                    setUserActivityPage(data.pagination?.page || 1);
                } else {
                    setUserActivityData(null);
                    if (data.message) setMessage(data.message);
                }
            })
            .catch(() => {
                if (active) setMessage("Gagal memuat aktivitas. Periksa koneksi dan coba lagi.");
            })
            .finally(() => {
                if (active) setBusy(false);
            });

        return () => {
            active = false;
        };
    }, [admin, activeConfig, userActivityUserId, userActivityPage]);

    const updateQuestionBankCoverageCourse = async (courseId) => {
        setQuestionBankCoverageCourseId(courseId);
        writeSessionValue(QUESTION_BANK_COVERAGE_COURSE_SESSION_KEY, courseId);
        await loadQuestionBankCoverage(courseId);
    };

    const handleLoginChange = (event) => {
        setLoginForm({...loginForm, [event.target.name]: event.target.value});
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        setBusy(true);
        setMessage("");
        const data = await apiPost("/admin/login", loginForm);
        if (data.admin) {
            setAdmin(data.admin);
            setLoginForm({username: "", password: ""});
        } else {
            setMessage(data.message || "Login admin gagal.");
        }
        setBusy(false);
    };

    const handleLogout = async () => {
        setBusy(true);
        await apiPost("/admin/logout", {});
        setAdmin(null);
        setRows([]);
        setRowsResource("");
        setEditingRow(null);
        setBusy(false);
        navigate(DEFAULT_PATH);
    };

    const handlePasswordChange = (event) => {
        setPasswordForm({...passwordForm, [event.target.name]: event.target.value});
    };

    const handleChangePassword = async (event) => {
        event.preventDefault();
        setMessage("");
        if (passwordForm.new_password !== passwordForm.confirm_password) {
            setMessage("New password dan confirmation password tidak sama.");
            return;
        }
        setBusy(true);
        const data = await apiPost("/admin/change-password", {
            current_password: passwordForm.current_password,
            new_password: passwordForm.new_password,
        });
        if (data.message) setMessage(data.message);
        if (data.loggedOut) {
            setAdmin(null);
            setRows([]);
            setRowsResource("");
            setPasswordForm({current_password: "", new_password: "", confirm_password: ""});
            navigate(DEFAULT_PATH);
        }
        setBusy(false);
    };

    const toggleMenu = (key) => {
        setOpenMenus((current) => ({...current, [key]: !current[key]}));
    };

    const scrollToTop = () => {
        window.scrollTo({top: 0, behavior: "smooth"});
    };

    const openAddForm = () => {
        setEditingRow(null);
        const nextForm = emptyForm(activeConfig);
        if (activeConfig?.resource === "topics" && topicCourseFilter) {
            nextForm.course_id = topicCourseFilter;
        }
        setFormData(nextForm);
        setMessage("");
    };

    const openEditForm = (row) => {
        setEditingRow(row);
        setFormData(buildForm(activeConfig, row));
        setMessage("");
    };

    const updateForm = (field, value) => {
        setFormData((current) => {
            const next = {...current, [field]: value};
            if (field === "course_id" && next.course_group_id) {
                const groupStillValid = (references.course_groups || []).some((group) => (
                    String(group.course_group_id) === String(next.course_group_id)
                    && String(group.course_id) === String(value)
                ));
                if (!groupStillValid) next.course_group_id = "";
            }
            if (activeConfig?.resource === "useradmins" && field === "role" && value === "admin") {
                next.user_id = "";
            }
            return next;
        });
    };

    const handleSave = async (event) => {
        event.preventDefault();
        if (!activeConfig) return;

        setBusy(true);
        setMessage("");
        const id = editingRow?.[activeConfig.idKey];
        const data = id
            ? await apiPatch(`/admin/resources/${activeConfig.resource}/${id}`, formData)
            : await apiPost(`/admin/resources/${activeConfig.resource}`, formData);

        if (data.message) setMessage(data.message);
        if (data.data) {
            setEditingRow(null);
            setFormData({});
            await Promise.all([loadRows(activeConfig), refreshReferences()]);
        }
        setBusy(false);
    };

    const handleDelete = async (row) => {
        if (!activeConfig || !activeConfig.canDelete) return;
        if (activeConfig.resource === "useradmins" && String(row.username || "").toLowerCase() === "admin") {
            setMessage("Default admin user tidak bisa dihapus.");
            return;
        }
        const label = row.username || row.group_name || row.course_name || row.topic_name || "data ini";
        if (!window.confirm(`Hapus ${label}? Data akan dihapus secara soft-delete.`)) return;

        setBusy(true);
        const data = await apiDelete(`/admin/resources/${activeConfig.resource}/${row[activeConfig.idKey]}`);
        if (data.message) setMessage(data.message);
        await Promise.all([loadRows(activeConfig), refreshReferences()]);
        setBusy(false);
    };

    const courseGroupOptions = (courseId = "") => (
        (references.course_groups || [])
            .filter((group) => !courseId || String(group.course_id) === String(courseId))
    );

    const updateTopicCourseFilter = (value) => {
        setTopicCourseFilter(value);
        writeSessionValue(TOPIC_ADMIN_COURSE_SESSION_KEY, value);
        setEditingRow(null);
        setFormData({});
    };

    const updateStudentBulk = (key, value) => {
        setStudentBulk((current) => {
            const next = {...current, [key]: value};
            if (key === "course_id") {
                next.course_group_id = "";
                next.target_course_group_id = "";
            }
            return next;
        });
        setSelectedStudentIds([]);
    };

    const toggleStudentSelection = (userId) => {
        setSelectedStudentIds((current) => (
            current.map(String).includes(String(userId))
                ? current.filter((id) => String(id) !== String(userId))
                : [...current, userId]
        ));
    };

    const setAllDisplayedStudentsSelected = (checked) => {
        setSelectedStudentIds(checked ? displayedRows.map((row) => row.user_id) : []);
    };

    const openTopicAssessmentReset = (topic) => {
        setTopicReset({
            topic_id: topic.topic_id,
            course_id: topic.course_id,
            course_group_id: "",
            search: "",
            activity_types: ["pre_test"],
            user_ids: [],
        });
        setMessage("");
    };

    const updateTopicReset = (key, value) => {
        setTopicReset((current) => ({
            ...current,
            [key]: value,
            user_ids: key === "course_group_id" || key === "search" ? current.user_ids : current.user_ids,
        }));
    };

    const toggleTopicResetActivity = (activityType) => {
        setTopicReset((current) => {
            const exists = current.activity_types.includes(activityType);
            const nextTypes = exists
                ? current.activity_types.filter((type) => type !== activityType)
                : [...current.activity_types, activityType];
            return {...current, activity_types: nextTypes};
        });
    };

    const toggleTopicResetStudent = (userId) => {
        setTopicReset((current) => {
            const selected = current.user_ids.map(String).includes(String(userId));
            return {
                ...current,
                user_ids: selected
                    ? current.user_ids.filter((id) => String(id) !== String(userId))
                    : [...current.user_ids, userId],
            };
        });
    };

    const setAllTopicResetStudentsSelected = (checked) => {
        setTopicReset((current) => ({
            ...current,
            user_ids: checked ? topicResetStudents.map((student) => student.user_id) : [],
        }));
    };

    const resetTopicAssessmentAttempts = async () => {
        if (!topicReset.topic_id || topicReset.user_ids.length === 0 || topicReset.activity_types.length === 0) {
            setMessage("Pilih assessment dan student terlebih dahulu.");
            return;
        }
        const typeLabel = topicReset.activity_types
            .map((type) => type === "pre_test" ? "Pre-test" : "Post-test")
            .join(" dan ");
        if (!window.confirm(`Hapus hasil ${typeLabel} untuk ${topicReset.user_ids.length} student terpilih? Data hasil yang dihapus tidak bisa dikembalikan.`)) return;

        setBusy(true);
        const data = await apiPost(`/admin/topics/${topicReset.topic_id}/reset-assessments`, {
            user_ids: topicReset.user_ids,
            activity_types: topicReset.activity_types,
        });
        if (data.message) setMessage(data.message);
        if (data.data) {
            setTopicReset((current) => ({...current, user_ids: []}));
        }
        setBusy(false);
    };

    const toggleBankSelection = (id) => {
        setSelectedBankIds((current) => (
            current.map(String).includes(String(id))
                ? current.filter((selectedId) => String(selectedId) !== String(id))
                : [...current, id]
        ));
    };

    const setAllPageBankSelected = (checked, ids) => {
        setSelectedBankIds((current) => {
            const pageIdStrings = ids.map(String);
            if (!checked) return current.filter((id) => !pageIdStrings.includes(String(id)));
            const merged = new Set([...current.map(String), ...pageIdStrings]);
            return Array.from(merged);
        });
    };

    const bulkDeleteBankItems = async (ids = selectedBankIds) => {
        if (!activeConfig?.bankType || ids.length === 0) return;
        if (!window.confirm(`Hapus ${ids.length} item question bank terpilih? Item akan dinonaktifkan dan tidak dipakai untuk aktivitas baru.`)) return;

        setBusy(true);
        const data = await apiPost(`/admin/question-bank/${activeConfig.bankType}/bulk-delete`, {ids});
        if (data.message) setMessage(data.message);
        if (data.data) {
            setSelectedBankIds([]);
            await loadBankRows(activeConfig.bankType);
        }
        setBusy(false);
    };

    const assignStudentsToGroup = async (userIds) => {
        if (!studentBulk.target_course_group_id || userIds.length === 0) return;
        setBusy(true);
        setMessage("");
        const data = await apiPatch("/admin/course-groups/students", {
            course_group_id: studentBulk.target_course_group_id,
            user_ids: userIds,
        });
        if (data.message) setMessage(data.message);
        if (data.data) {
            setSelectedStudentIds([]);
            await Promise.all([loadRows(activeConfig), refreshReferences()]);
        }
        setBusy(false);
    };

    const renderReferenceOptions = (field) => {
        let options = references[field.reference] || [];
        if (field.dependsOn && formData[field.dependsOn]) {
            options = options.filter((option) => String(option[field.dependsOn]) === String(formData[field.dependsOn]));
        }
        if (field.reference === "useradmin_users" && formData.role === "instructor") {
            options = options.filter((option) => String(option.role_name || "").toLowerCase() === "instructor");
        }
        return options.map((option) => {
            const value = field.valueKey ? option[field.valueKey] : option.instructor_id ?? option.course_id;
            const label = field.labelKey
                ? option[field.labelKey]
                : option.instructor_name || option.course_name;
            return (
                <option key={value} value={value}>
                    {field.reference === "course_groups"
                        ? `${option.course_name} - ${label}`
                        : field.reference === "useradmin_users"
                            ? `${label}${option.email ? ` (${option.email})` : ""}`
                            : label}
                </option>
            );
        });
    };

    const renderTopicOptions = () => (
        (references.topics || []).map((topic) => (
            <option key={topic.topic_id} value={topic.topic_id}>
                {topic.course_name} - {topic.topic_name}
            </option>
        ))
    );

    const topicOptionsForCourse = (courseId = "") => (
        (references.topics || []).filter((topic) => !courseId || String(topic.course_id) === String(courseId))
    );

    const updateBankFilter = (key, value) => {
        setBankFilters((current) => {
            const next = {...current, [key]: value};
            if (key === "course_id") next.topic_id = "";
            writeBankFiltersSession(activeConfig?.bankType, next);
            return next;
        });
        setBankPage(1);
        setSelectedBankIds([]);
    };

    const updateMaterialTopicFilter = (value) => {
        setMaterialTopicFilter(value);
        writeSessionValue(QUESTION_BANK_TOPIC_SESSION_KEY, value);
        setQuestionSettings((current) => ({
            ...current,
            topic_id: value,
            material_ids: [],
        }));
        setDrafts([]);
        setDraftMeta(null);
    };

    const renderField = (field) => {
        const value = formData[field.key];
        const selectedRole = (references.roles || []).find((role) => String(role.role_id) === String(formData.role_id));
        const isInstructorSelected = String(selectedRole?.role_name || "").toLowerCase() === "instructor";
        const isOptionalInstructorGroup = activeConfig?.resource === "students"
            && field.key === "course_group_id"
            && isInstructorSelected;

        if (field.type === "select") {
            const options = field.options || null;
            return (
                <select
                    value={value}
                    onChange={(event) => updateForm(field.key, event.target.value)}
                    required={isOptionalInstructorGroup ? false : field.required}
                >
                    <option value="">Choose {field.label}</option>
                    {options
                        ? options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)
                        : renderReferenceOptions(field)}
                </select>
            );
        }

        if (field.type === "checkbox") {
            return (
                <label className="admin-checkbox">
                    <input
                        type="checkbox"
                        checked={value !== false}
                        onChange={(event) => updateForm(field.key, event.target.checked)}
                    />
                    <span>{value !== false
                        ? field.trueLabel || "Shown to students"
                        : field.falseLabel || "Hidden from students"}</span>
                </label>
            );
        }

        return (
            <input
                type={field.type || "text"}
                value={value ?? ""}
                placeholder={field.placeholder || ""}
                onChange={(event) => updateForm(field.key, event.target.value)}
                required={field.required || (field.requiredOnCreate && !editingRow)}
            />
        );
    };

    const isFormFieldVisible = (field) => {
        if (!field.visibleForSettingTypes) return true;
        const settingType = editingRow?.setting_type || formData.setting_type;
        return field.visibleForSettingTypes.includes(settingType);
    };

    const saveMaterial = async (event) => {
        event.preventDefault();
        setBusy(true);
        setMessage("");
        const data = editingMaterial
            ? await apiPatch(`/admin/materials/${editingMaterial.material_id}`, materialForm)
            : await apiPost("/admin/materials", materialForm);
        if (data.message) setMessage(data.message);
        if (data.data) {
            setEditingMaterial(null);
            setMaterialForm({topic_id: "", title: "", content_text: ""});
            await loadMaterials();
        }
        setBusy(false);
    };

    const editMaterial = (material) => {
        setEditingMaterial(material);
        setMaterialForm({
            topic_id: material.topic_id,
            title: material.title,
            content_text: material.content_text,
        });
    };

    const removeMaterial = async (material) => {
        if (!window.confirm(`Hapus materi "${material.title}"?`)) return;
        setBusy(true);
        const data = await apiDelete(`/admin/materials/${material.material_id}`);
        if (data.message) setMessage(data.message);
        await loadMaterials();
        setBusy(false);
    };

    const generateDigest = async (material) => {
        setBusy(true);
        setMessage("Generating compact digest from material...");
        const data = await apiPost(`/admin/materials/${material.material_id}/digest`, {});
        if (data.message) setMessage(data.message);
        await loadMaterials();
        setBusy(false);
    };

    const updateQuestionSettings = async (key, value) => {
        const next = {...questionSettings, [key]: value};
        if (key === "topic_id") {
            next.material_ids = [];
            setMaterialTopicFilter(value);
            writeSessionValue(QUESTION_BANK_TOPIC_SESSION_KEY, value);
        }
        if (key === "bank_type" && value === "quiz_question_bank") {
            next.activity_type = "exercise";
            next.question_kind = "multiple_choice";
            next.individual_question_type = "exercise_multiple_choice";
            next.count = 5;
        }
        if (key === "bank_type" && value === "topic_cases") {
            next.activity_type = "exercise";
            next.question_kind = "case_study";
            next.individual_question_type = "exercise_case_study";
            next.count = 1;
        }
        if (key === "bank_type" && value === "individual_questions") {
            const type = next.individual_question_type || "exercise_multiple_choice";
            next.activity_type = getActivityFromType(type);
            next.question_kind = getQuestionKindFromType(type);
            next.count = getQuestionKindFromType(type) === "case_study" ? 1 : 5;
        }
        if (key === "individual_question_type") {
            next.activity_type = getActivityFromType(value);
            next.question_kind = getQuestionKindFromType(value);
            next.count = getQuestionKindFromType(value) === "case_study" ? Math.min(Number(next.count) || 1, 15) : next.count;
        }
        setQuestionSettings(next);
        setDrafts([]);
        setDraftMeta(null);
    };

    const applyInstructionPreset = (preset) => {
        setQuestionSettings((current) => ({
            ...current,
            custom_instruction: preset.instruction_text || "",
        }));
        setDrafts([]);
        setDraftMeta(null);
    };

    const saveInstructionPreset = async () => {
        const presetName = instructionPresetForm.preset_name.trim();
        const instructionText = instructionPresetForm.instruction_text.trim();
        if (!presetName || !instructionText) {
            setMessage("Isi nama preset dan instruction preset terlebih dahulu.");
            return;
        }

        setBusy(true);
        setMessage("");
        try {
            const payload = {preset_name: presetName, instruction_text: instructionText};
            const data = editingInstructionPresetId
                ? await apiPatch(`/admin/question-instruction-presets/${editingInstructionPresetId}`, payload)
                : await apiPost("/admin/question-instruction-presets", payload);
            if (data.message) setMessage(data.message);
            if (data.data) {
                setEditingInstructionPresetId(null);
                setInstructionPresetForm({preset_name: "", instruction_text: ""});
                await loadInstructionPresets();
            }
        } catch (error) {
            setMessage("Gagal menyimpan preset instruction. Periksa koneksi dan coba lagi.");
        } finally {
            setBusy(false);
        }
    };

    const deleteInstructionPreset = async (preset) => {
        if (!window.confirm(`Hapus preset "${preset.preset_name}"?`)) return;
        setBusy(true);
        const data = await apiDelete(`/admin/question-instruction-presets/${preset.preset_id}`);
        if (data.message) setMessage(data.message);
        if (data.data && editingInstructionPresetId === preset.preset_id) {
            setEditingInstructionPresetId(null);
            setInstructionPresetForm({preset_name: "", instruction_text: ""});
        }
        await loadInstructionPresets();
        setBusy(false);
    };

    const updateQuestionBankTarget = (value) => {
        const target = QUESTION_BANK_TARGETS.find((item) => item.value === value) || QUESTION_BANK_TARGETS[0];
        const next = {
            ...questionSettings,
            bank_type: target.bank_type,
            count: target.count,
        };

        if (target.bank_type === "individual_questions") {
            next.individual_question_type = target.individual_question_type;
            next.activity_type = getActivityFromType(target.individual_question_type);
            next.question_kind = getQuestionKindFromType(target.individual_question_type);
        } else if (target.bank_type === "topic_cases") {
            next.activity_type = "exercise";
            next.question_kind = "case_study";
            next.individual_question_type = "exercise_case_study";
        } else {
            next.activity_type = "exercise";
            next.question_kind = "multiple_choice";
            next.individual_question_type = "exercise_multiple_choice";
        }

        setQuestionSettings(next);
        setDrafts([]);
        setDraftMeta(null);
    };

    const toggleMaterialSelection = (materialId) => {
        setQuestionSettings((current) => {
            const currentIds = current.material_ids || [];
            const id = String(materialId);
            const nextIds = currentIds.map(String).includes(id)
                ? currentIds.filter((item) => String(item) !== id)
                : [...currentIds, id];
            return {...current, material_ids: nextIds};
        });
        setDrafts([]);
        setDraftMeta(null);
    };

    const generateDrafts = async (event) => {
        event.preventDefault();
        setBusy(true);
        setMessage("Generating question drafts from saved digest/material...");
        try {
            const data = await apiPost("/admin/question-bank/generate", questionSettings);
            if (data.message) setMessage(data.message);
            setDrafts(data.items || []);
            setDraftMeta(data.items ? data : null);
        } catch (error) {
            setMessage("Gagal membuat draft question. Koneksi server terputus atau proses terlalu lama.");
            setDrafts([]);
            setDraftMeta(null);
        } finally {
            setBusy(false);
        }
    };

    const getBankQuestionType = () => {
        if (questionSettings.bank_type === "topic_cases") return "case_study";
        if (questionSettings.bank_type === "individual_questions") {
            return getQuestionKindFromType(questionSettings.individual_question_type);
        }
        return "multiple_choice";
    };

    const updateDraft = (index, key, value) => {
        setDrafts((current) => current.map((item, itemIndex) => (
            itemIndex === index ? {...item, [key]: value} : item
        )));
    };

    const updateDraftChoice = (index, choiceIndex, value) => {
        setDrafts((current) => current.map((item, itemIndex) => {
            if (itemIndex !== index) return item;
            const choices = [...(item.choices || [])];
            choices[choiceIndex] = value;
            return {...item, choices};
        }));
    };

    const removeDraft = (index) => {
        setDrafts((current) => current.filter((_, itemIndex) => itemIndex !== index));
    };

    const saveDrafts = async () => {
        setBusy(true);
        const data = await apiPost("/admin/question-bank/save", {
            ...questionSettings,
            items: drafts,
        });
        if (data.message) setMessage(data.message);
        setDrafts([]);
        setDraftMeta(null);
        setBusy(false);
    };

    const emptyBankForm = (bankType) => {
        if (bankType === "quiz_question_bank") {
            return {
                topic_id: "",
                question_number: "",
                question_text: "",
                choices_text: "",
                correct_answer_index: 0,
                explanation: "",
            };
        }
        if (bankType === "individual_questions") {
            return {
                topic_id: "",
                individual_question_type: "exercise_multiple_choice",
                activity_type: "exercise",
                question_kind: "multiple_choice",
                question_number: "",
                question_text: "",
                choices_text: "",
                correct_answer_index: 0,
                explanation: "",
                case_title: "",
                case_prompt: "",
            };
        }
        return {
            topic_id: "",
            case_number: 1,
            case_title: "",
            case_prompt: "",
        };
    };

    const buildBankForm = (bankType, row) => {
        if (bankType === "quiz_question_bank") {
            return {
                topic_id: row.topic_id,
                question_number: row.question_number,
                question_text: row.question_text || "",
                choices_text: (row.choices || []).join("\n"),
                correct_answer_index: row.correct_answer_index ?? 0,
                explanation: row.explanation || "",
            };
        }
        if (bankType === "individual_questions") {
            const type = getIndividualTypeFromRow(row);
            return {
                topic_id: row.topic_id,
                individual_question_type: type,
                activity_type: getActivityFromType(type),
                question_kind: getQuestionKindFromType(type),
                question_number: row.question_number,
                question_text: row.question_text || "",
                choices_text: (row.choices || []).join("\n"),
                correct_answer_index: row.correct_answer_index ?? 0,
                explanation: row.explanation || "",
                case_title: row.case_title || "",
                case_prompt: row.case_prompt || "",
            };
        }
        return {
            topic_id: row.topic_id,
            case_number: row.case_number,
            case_title: row.case_title || "",
            case_prompt: row.case_prompt || "",
        };
    };

    const openBankAddForm = () => {
        setEditingBankRow(null);
        setBankForm(emptyBankForm(activeConfig.bankType));
        setMessage("");
    };

    const openBankEditForm = (row) => {
        setEditingBankRow(row);
        setBankForm(buildBankForm(activeConfig.bankType, row));
        setMessage("");
    };

    const updateBankForm = (key, value) => {
        setBankForm((current) => {
            const next = {...current, [key]: value};
            if (key === "individual_question_type") {
                next.activity_type = getActivityFromType(value);
                next.question_kind = getQuestionKindFromType(value);
            }
            return next;
        });
    };

    const saveBankItem = async (event) => {
        event.preventDefault();
        const bankType = activeConfig.bankType;
        const config = BANK_CONFIGS[bankType];
        const id = editingBankRow?.[config.idKey];
        const payload = {
            ...bankForm,
            activity_type: bankType === "individual_questions"
                ? getActivityFromType(bankForm.individual_question_type)
                : bankForm.activity_type,
            question_kind: bankType === "individual_questions"
                ? getQuestionKindFromType(bankForm.individual_question_type)
                : bankType === "topic_cases" ? "case_study" : "multiple_choice",
        };
        setBusy(true);
        setMessage("");
        const data = id
            ? await apiPatch(`/admin/question-bank/${bankType}/${id}`, payload)
            : await apiPost(`/admin/question-bank/${bankType}`, payload);
        if (data.message) setMessage(data.message);
        if (data.data) {
            setBankForm({});
            setEditingBankRow(null);
            await loadBankRows(bankType);
        }
        setBusy(false);
    };

    const renderChoicesPreview = (choices = [], correctAnswerIndex = null) => (
        <ol className="admin-choice-preview">
            {(choices || []).map((choice, index) => {
                const isCorrect = Number(correctAnswerIndex) === index;
                return (
                    <li className={isCorrect ? "is-correct" : ""} key={index}>
                        <MarkdownText>{choice}</MarkdownText>
                        {isCorrect && <b>Correct</b>}
                    </li>
                );
            })}
        </ol>
    );

    const exportSelectedTopicAssessments = () => {
        const rows = (selectedDashboardTopic?.groups || []).flatMap((group) => (
            (group.assessment_comparison || []).map((student) => {
                const groupStudent = (group.students || []).find((item) => String(item.user_id) === String(student.user_id));
                return {
                    group_name: group.group_name || "Ungrouped",
                    student_email: groupStudent?.email || "",
                    student_name: student.student_name,
                    pre_score: student.pre_score ?? "",
                    post_score: student.post_score ?? "",
                    improvement: student.improvement ?? "",
                };
            })
        ));

        if (!rows.length) {
            setMessage("No pre-test and post-test data available for this topic.");
            return;
        }

        const workbookRows = [
            ["Course", "Topic", "Group", "Student Email", "Student", "Pre-test Score", "Post-test Score", "Improvement"],
            ...rows.map((row) => [
                selectedDashboardCourse?.course_name || "",
                selectedDashboardTopic?.topic_name || "",
                row.group_name,
                row.student_email,
                row.student_name,
                row.pre_score === "" ? "" : Number(row.pre_score),
                row.post_score === "" ? "" : Number(row.post_score),
                row.improvement === "" ? "" : Number(row.improvement),
            ]),
        ];
        const blob = createXlsxBlob(workbookRows, "Pre Post Scores");
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${slugifyFilePart(selectedDashboardCourse?.course_name)}-${slugifyFilePart(selectedDashboardTopic?.topic_name)}-pre-post.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const exportSelectedTopicSummary = async () => {
        if (!selectedDashboardCourse?.course_id || !selectedDashboardTopic?.topic_id || topicSummaryDownloading) return;
        const course = selectedDashboardCourse;
        const topic = selectedDashboardTopic;
        setTopicSummaryDownloading(true);
        setMessage("");
        try {
            const data = await apiGet(`/instructor/courses/${course.course_id}/topics/${topic.topic_id}/summary-export`);
            if (!Array.isArray(data.sheets) || !data.sheets.length) {
                setMessage(data.message || "Gagal membuat ringkasan topic.");
                return;
            }
            const [first, ...rest] = data.sheets;
            const blob = createXlsxBlob(first.rows, first.name, rest);
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${slugifyFilePart(course.course_name)}-${slugifyFilePart(topic.topic_name)}-topic-summary.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (error) {
            setMessage("Gagal mengunduh ringkasan topic. Periksa koneksi dan coba lagi.");
        } finally {
            setTopicSummaryDownloading(false);
        }
    };

    const exportSelectedTopicActivityProgress = (pendingOnly = false) => {
        const rows = (selectedDashboardTopic?.groups || []).flatMap((group) => {
            const topic = topicProgressForGroup(selectedDashboardCourse, group)
                .find((item) => String(item.key) === `topic:${selectedDashboardTopic?.topic_id}`);
            if (!topic) return [];
            const pendingRows = topic.students_pending.map((student) => ({student, group, status: "belum memenuhi", isPending: true}));
            if (pendingOnly) return pendingRows;
            return [
                ...topic.students_met.map((student) => ({student, group, status: "sudah memenuhi", isPending: false})),
                ...pendingRows,
            ];
        });

        if (!rows.length) {
            setMessage(pendingOnly
                ? "No students with belum memenuhi status for this topic."
                : "No activity progress data available for this topic.");
            return;
        }

        const workbookRows = [
            ["Student Name", "Email", "Group", "Status", "Activities", "Individual MC", "Individual Case", "Group Activity", "Fun Quiz"]
                .map((value) => ({value, style: "header"})),
            ...rows.map(({student, group, status, isPending}) => {
                const activitySummary = Object.entries(WEEKLY_BASELINE)
                    .map(([kind, required]) => `${weeklyKindLabel(kind)}: ${student.counts[kind] || 0}/${required}`)
                    .join("; ");
                return [
                    student.name || "",
                    student.email || "",
                    group.group_name || "Ungrouped",
                    {value: status, style: isPending ? "danger" : ""},
                    activitySummary,
                    Number(student.counts.individual_mc || 0),
                    Number(student.counts.individual_case || 0),
                    Number(student.counts.group || 0),
                    Number(student.counts.quiz || 0),
                ];
            }),
        ];

        const blob = createXlsxBlob(workbookRows, "Activity Progress");
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${slugifyFilePart(selectedDashboardCourse?.course_name)}-${slugifyFilePart(selectedDashboardTopic?.topic_name)}-${pendingOnly ? "belum-memenuhi" : "activity-progress"}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const renderTopicActivityProgress = (group) => {
        const topic = topicProgressForGroup(selectedDashboardCourse, group)
            .find((item) => String(item.key) === `topic:${selectedDashboardTopic?.topic_id}`);
        if (!topic) {
            return <p>No topic activity data available for this group yet.</p>;
        }
        const totalSessions = Object.values(topic.totals).reduce((total, value) => total + value, 0);
        return (
            <div className="weekly-progress weekly-progress--summary">
                <div className="weekly-progress-note">
                    Activity totals are counted by completed/saved sessions. Student baseline status is counted by each student's participation.
                </div>
                <div className="weekly-session-totals">
                    <article className="weekly-session-total-card">
                        <span>Total Sessions</span>
                        <strong>{formatAdminNumber(totalSessions)}</strong>
                        <small>completed/saved</small>
                    </article>
                    {Object.entries(WEEKLY_BASELINE).map(([kind]) => (
                        <article className={`weekly-session-total-card weekly-session-total-card--${kind}`} key={kind}>
                            <span>{weeklyKindLabel(kind)}</span>
                            <strong>{formatAdminNumber(topic.totals[kind])}</strong>
                            <small>sessions</small>
                        </article>
                    ))}
                </div>
                <div className="weekly-student-grid weekly-student-grid--summary">
                    <section className="weekly-student-list weekly-student-list--met">
                        <header>
                            <strong>Baseline met</strong>
                            <span>{formatAdminNumber(topic.students_met.length)} students</span>
                        </header>
                        <strong className="weekly-student-total">{formatAdminNumber(topic.students_met.length)}</strong>
                    </section>
                    <section className="weekly-student-list weekly-student-list--pending">
                        <header>
                            <strong>Needs progress</strong>
                            <span>{formatAdminNumber(topic.students_pending.length)} students</span>
                        </header>
                        <strong className="weekly-student-total">{formatAdminNumber(topic.students_pending.length)}</strong>
                    </section>
                </div>
            </div>
        );
    };
    const renderMissingAssessmentList = (title, students) => (
        <section className="missing-assessment-section">
            <header>
                <strong>{title}</strong>
                <span>{formatAdminNumber(students.length)} students</span>
            </header>
            <div>
                {students.map((student) => (
                    <article key={student.user_id}>
                        <div className="student-identity">
                            <b>{student.name}</b>
                            <small>{student.email || "No email"}</small>
                        </div>
                    </article>
                ))}
                {students.length === 0 && <p>All students have completed this assessment.</p>}
            </div>
        </section>
    );

    const renderGroupComparison = (groups) => {
        const rows = topicComparisonRows(groups, selectedDashboardTopic?.topic_id);
        const maxStudents = Math.max(0, ...groups.map((group) => Number(group.student_count || group.students?.length || 0)));
        const xpComparisonTypes = [
            ["Individual XP", "individualXp"],
            ["Shared Group XP", "sharedGroupXp"],
            ["Students Group XP", "studentsEarnedGroupXp"],
        ];

        return (
            <section className="instructor-comparison">
                <div className="instructor-comparison-header">
                    <div>
                        <span>Between Groups</span>
                        <h3>Topic comparison</h3>
                    </div>
                    <small>Group and quiz activity counts are counted by saved session.</small>
                </div>

                <div className="instructor-comparison-grid">
                    <article className="comparison-panel">
                        <h4>XP difference</h4>
                        <div className="comparison-bars">
                            {rows.map((row) => (
                                <div className="comparison-xp-group" key={`xp-${row.group.course_group_id}`}>
                                    <header>
                                        <strong>{row.groupName}</strong>
                                    </header>
                                    <div className="comparison-xp-values">
                                        <article className={row.topXpTypes.individualXp ? "is-top-xp" : ""}>
                                            <span>Individual XP</span>
                                            <b>{formatAdminNumber(row.individualXp)} XP</b>
                                            {row.topXpTypes.individualXp && <small>Highest</small>}
                                        </article>
                                        <article className={row.topXpTypes.sharedGroupXp ? "is-top-xp" : ""}>
                                            <span>Shared Group XP</span>
                                            <b>{formatAdminNumber(row.sharedGroupXp)} XP</b>
                                            {row.topXpTypes.sharedGroupXp && <small>Highest</small>}
                                        </article>
                                        <article className={row.topXpTypes.studentsEarnedGroupXp ? "is-top-xp" : ""}>
                                            <span>Students Group XP</span>
                                            <b>{formatAdminNumber(row.studentsEarnedGroupXp)} XP</b>
                                            {row.topXpTypes.studentsEarnedGroupXp && <small>Highest</small>}
                                        </article>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="comparison-xp-percentages">
                            <h5>Percentage from total XP type</h5>
                            {rows.map((row) => (
                                <div className="comparison-xp-percent-group" key={`xp-percent-${row.group.course_group_id}`}>
                                    <strong>{row.groupName}</strong>
                                    <div>
                                        {xpComparisonTypes.map(([label, key]) => (
                                            <span className={row.topXpTypes[key] ? "is-top-xp" : ""} key={key}>
                                                {label}: <b>{row.xpTypePercentages[key]}%</b>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </article>

                    <article className="comparison-panel">
                        <h4>Activities done</h4>
                        <div className="comparison-activity-list">
                            {rows.map((row) => (
                                <div className="comparison-activity-row" key={`activity-${row.group.course_group_id}`}>
                                    <strong>{row.groupName}</strong>
                                    {[
                                        ["Individual MC", row.activityCounts.individual_mc, "individual"],
                                        ["Individual Case", row.activityCounts.individual_case, "case"],
                                        ["Group", row.activityCounts.group, "group"],
                                        ["Quiz", row.activityCounts.quiz, "quiz"],
                                    ].map(([label, value, className]) => (
                                        <div key={label}>
                                            <span>{label}</span>
                                            <i className={`comparison-mini-bar comparison-mini-bar--${className}`} style={{width: `${barWidth(value, row.totalActivities)}%`}}/>
                                            <b>{percentage(value, row.totalActivities)}%</b>
                                            <small>{formatAdminNumber(value)} of {formatAdminNumber(row.totalActivities)}</small>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </article>

                    <article className="comparison-panel comparison-panel--wide">
                        <h4>Students by level</h4>
                        <div className="comparison-levels">
                            {groups.map((group) => {
                                const groupTotal = Number(group.student_count || group.students?.length || 0);
                                const topicLevels = topicLevelDistribution(group, selectedDashboardTopic?.topic_id);
                                return (
                                    <div className="comparison-level-group" key={`level-${group.course_group_id}`}>
                                        <header>
                                            <strong>{group.group_name || "Ungrouped"}</strong>
                                            <span>{formatAdminNumber(groupTotal)} students</span>
                                        </header>
                                        {topicLevels.map((level) => {
                                            const count = level.students?.length || 0;
                                            const percent = percentage(count, groupTotal || maxStudents);
                                            return (
                                                <div className="comparison-level-row" key={`${group.course_group_id}-${level.level_id}`}>
                                                    <span>{level.level_name}</span>
                                                    <div>
                                                        <i style={{width: `${barWidth(count, groupTotal || maxStudents)}%`, background: level.color_hex}}/>
                                                    </div>
                                                    <b>{percent}%</b>
                                                    <small>{level.students.map((student) => `${student.name} (${formatAdminNumber(student.topic_xp)} XP)`).join(", ")}</small>
                                                </div>
                                            );
                                        })}
                                        {topicLevels.length === 0 && (
                                            <p>No level data yet.</p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </article>
                </div>
            </section>
        );
    };

    const renderInstructorDashboard = () => {
        if (!instructorDashboard && busy) {
            return <div className="admin-empty-dashboard"><h1>Loading dashboard...</h1></div>;
        }
        if (!dashboardCourses.length) {
            return (
                <div className="instructor-analytics">
                    <section className="instructor-analytics-hero">
                        <div>
                            <span>Instructor Dashboard</span>
                            <h1>No managed course found</h1>
                            <p>Courses will appear here when this instructor account is assigned as Instructor 1 or Instructor 2.</p>
                        </div>
                    </section>
                </div>
            );
        }

        const topicGroups = selectedDashboardTopic?.groups || [];
        const selectedAssessmentCount = topicGroups.reduce((total, group) => total + (group.assessment_comparison?.length || 0), 0);
        const selectedTopicCounts = topicGroups.reduce((totals, group) => {
            const counts = group.activity_counts || {};
            totals.activities += Number(counts.individual || 0) + Number(counts.group || 0) + Number(counts.quiz || 0);
            totals.preTest += Number(counts.pre_test || 0);
            totals.postTest += Number(counts.post_test || 0);
            return totals;
        }, {activities: 0, preTest: 0, postTest: 0});
        const changedCourse = (courseId) => {
            const nextCourse = dashboardCourses.find((course) => String(course.course_id) === String(courseId));
            setSelectedDashboardCourseId(courseId);
            writeDashboardCourseSession(courseId);
            setSelectedDashboardTopicId(nextCourse?.topics?.[0]?.topic_id || "");
        };

        return (
            <div className="instructor-analytics">
                <section className="instructor-analytics-hero">
                    <div>
                        <span>Instructor Dashboard</span>
                        <h1>{selectedDashboardCourse?.course_name || "Course Activity"}</h1>
                        <p>
                            Saved learning activity, XP contribution, level progress, and assessment improvement by topic and group.
                        </p>
                    </div>
                    <div className="instructor-dashboard-controls">
                        <label>
                            Course
                            <select
                                value={selectedDashboardCourse?.course_id || ""}
                                onChange={(event) => changedCourse(event.target.value)}
                            >
                                {dashboardCourses.map((course) => (
                                    <option key={course.course_id} value={course.course_id}>
                                        {course.course_name}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <small>Updated {formatAdminDate(instructorDashboard?.generated_at)}</small>
                    </div>
                </section>

                <section className="instructor-topic-picker" aria-label="Topic filter">
                    <label>
                        Topic
                        <select
                            value={selectedDashboardTopic?.topic_id || ""}
                            onChange={(event) => setSelectedDashboardTopicId(event.target.value)}
                            disabled={!selectedDashboardCourse?.topics?.length}
                        >
                            {(selectedDashboardCourse?.topics || []).map((topic) => (
                                <option key={topic.topic_id} value={topic.topic_id}>
                                    {topic.week ? `Week ${topic.week} - ` : ""}{topic.topic_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <span>{formatAdminNumber(selectedDashboardCourse?.topics?.length || 0)} topics available</span>
                </section>

                <section className="instructor-topic-panel">
                    <div className="instructor-topic-header">
                        <div>
                            <span>{selectedDashboardTopic?.week ? `Week ${selectedDashboardTopic.week}` : "Selected Topic"}</span>
                            <h2>{selectedDashboardTopic?.topic_name || "Topic"}</h2>
                        </div>
                        <div className="instructor-topic-stats">
                            <b className="topic-stat topic-stat--activities">{formatAdminNumber(selectedTopicCounts.activities)} activities</b>
                            <b className="topic-stat topic-stat--pre">{formatAdminNumber(selectedTopicCounts.preTest)} pre-test</b>
                            <b className="topic-stat topic-stat--post">{formatAdminNumber(selectedTopicCounts.postTest)} post-test</b>
                            <b className="topic-stat topic-stat--group-xp">{formatAdminNumber(selectedDashboardTopic?.summary?.total_group_xp)} group XP</b>
                            <b className="topic-stat topic-stat--individual-xp">{formatAdminNumber(selectedDashboardTopic?.summary?.total_individual_xp)} individual XP</b>
                        </div>
                    </div>

                    <div className="instructor-topic-actions">
                        <div>
                            <strong>Pre-test & post-test export</strong>
                            <span>{formatAdminNumber(selectedAssessmentCount)} student comparisons in this topic</span>
                        </div>
                        <button type="button" onClick={exportSelectedTopicAssessments}>
                            Export Excel
                        </button>
                    </div>
                    {message && <div className="admin-inline-message">{message}</div>}

                    {renderGroupComparison(topicGroups)}

                    <div className="instructor-topic-actions instructor-topic-actions--activity">
                        <div>
                            <strong>Activity progress export</strong>
                            <span>Download baseline status and activity counts for all groups in this topic</span>
                        </div>
                        <div className="instructor-topic-action-buttons">
                            <button type="button" onClick={exportSelectedTopicSummary} disabled={topicSummaryDownloading}>
                                {topicSummaryDownloading ? "Preparing Topic Summary..." : "Download Topic Summary"}
                            </button>
                            <button type="button" onClick={() => exportSelectedTopicActivityProgress(false)}>
                                Download Activity Progress
                            </button>
                            <button type="button" onClick={() => exportSelectedTopicActivityProgress(true)}>
                                Download Belum Memenuhi
                            </button>
                        </div>
                    </div>

                    <div className="instructor-group-list">
                        {topicGroups.map((group) => (
                            <article
                                className={`instructor-group-detail ${group.gamification_enabled ? "has-game" : ""}`}
                                key={`${group.topic_id}-${group.course_group_id}`}
                            >
                                <header>
                                    <div>
                                        <span>{group.gamification_enabled ? "Gamification enabled" : "Gamification disabled"}</span>
                                        <h3>{group.group_name || "Ungrouped"}</h3>
                                    </div>
                                </header>

                                <section className="instructor-topic-activity-detail">
                                    <h4>Activity detail</h4>
                                    {renderTopicActivityProgress(group)}
                                </section>

                                <details>
                                    <summary>Pre-test vs post-test</summary>
                                    <div className="score-chart">
                                        {(group.assessment_comparison || []).map((student) => (
                                            <div className="score-chart-row" key={student.user_id}>
                                                <span>{student.student_name}</span>
                                                <div>
                                                    <i className="score-pre" style={{width: `${scoreWidth(student.pre_score)}%`}}/>
                                                    <i className="score-post" style={{width: `${scoreWidth(student.post_score)}%`}}/>
                                                </div>
                                                <b>{student.pre_score ?? "-"} / {student.post_score ?? "-"}</b>
                                                <small>{student.improvement > 0 ? "+" : ""}{student.improvement ?? "-"}</small>
                                            </div>
                                        ))}
                                        {(!group.assessment_comparison || group.assessment_comparison.length === 0) && <p>No pre/post score pair recorded yet.</p>}
                                    </div>
                                    <div className="missing-assessment-grid">
                                        {renderMissingAssessmentList("Not yet done pre-test", missingAssessmentStudents(group).preTest)}
                                        {renderMissingAssessmentList("Not yet done post-test", missingAssessmentStudents(group).postTest)}
                                    </div>
                                </details>
                            </article>
                        ))}
                    </div>
                </section>
            </div>
        );
    };

    const renderStudentBulkPanel = () => {
        if (activeConfig?.resource !== "students") return null;
        const targetGroups = courseGroupOptions(studentBulk.course_id);
        const selectedCount = selectedStudentIds.length;
        return (
            <section className="admin-bulk-panel">
                <div>
                    <h2>Bulk Group Assignment</h2>
                    <p>Filter students, select rows, then move them to a course group in one action.</p>
                </div>
                <div className="admin-bulk-grid">
                    <label>
                        Course Filter
                        <select
                            value={studentBulk.course_id}
                            onChange={(event) => updateStudentBulk("course_id", event.target.value)}
                        >
                            <option value="">All courses</option>
                            {(references.courses || []).map((course) => (
                                <option key={course.course_id} value={course.course_id}>
                                    {course.course_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Current Group
                        <select
                            value={studentBulk.course_group_id}
                            onChange={(event) => updateStudentBulk("course_group_id", event.target.value)}
                        >
                            <option value="">Any group</option>
                            {courseGroupOptions(studentBulk.course_id).map((group) => (
                                <option key={group.course_group_id} value={group.course_group_id}>
                                    {group.course_name} - {group.group_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Search
                        <input
                            value={studentBulk.search}
                            onChange={(event) => updateStudentBulk("search", event.target.value)}
                            placeholder="Name or email"
                        />
                    </label>
                    <label>
                        Target Group
                        <select
                            value={studentBulk.target_course_group_id}
                            onChange={(event) => updateStudentBulk("target_course_group_id", event.target.value)}
                        >
                            <option value="">Choose target group</option>
                            {targetGroups.map((group) => (
                                <option key={group.course_group_id} value={group.course_group_id}>
                                    {group.course_name} - {group.group_name}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
                <div className="admin-bulk-actions">
                    <span>{displayedRows.length} shown, {selectedCount} selected</span>
                    <button
                        type="button"
                        disabled={busy || !studentBulk.target_course_group_id || selectedCount === 0}
                        onClick={() => assignStudentsToGroup(selectedStudentIds)}
                    >
                        Assign Selected
                    </button>
                    <button
                        type="button"
                        disabled={busy || !studentBulk.target_course_group_id || displayedRows.length === 0}
                        onClick={() => assignStudentsToGroup(displayedRows.map((row) => row.user_id))}
                    >
                        Assign All Shown
                    </button>
                </div>
            </section>
        );
    };

    const renderTopicCourseFilter = () => {
        if (activeConfig?.resource !== "topics") return null;
        const courses = references.courses || [];
        const selectedCourse = courses.find((course) => String(course.course_id) === String(topicCourseFilter));

        return (
            <section className="admin-filter-panel">
                <label>
                    Course Filter
                    <select
                        value={topicCourseFilter}
                        onChange={(event) => updateTopicCourseFilter(event.target.value)}
                    >
                        {courses.map((course) => (
                            <option key={course.course_id} value={course.course_id}>
                                {course.course_name}
                            </option>
                        ))}
                    </select>
                </label>
                <span>
                    Showing {displayedRows.length} topic{displayedRows.length === 1 ? "" : "s"}
                    {selectedCourse ? ` for ${selectedCourse.course_name}` : ""}
                </span>
            </section>
        );
    };

    const renderTopicResetPanel = () => {
        if (activeConfig?.resource !== "topics" || !topicReset.topic_id) return null;
        const topic = rows.find((row) => String(row.topic_id) === String(topicReset.topic_id));
        const groups = courseGroupOptions(topicReset.course_id);
        const selectedCount = topicReset.user_ids.length;
        const allShownSelected = topicResetStudents.length > 0
            && topicResetStudents.every((student) => topicReset.user_ids.map(String).includes(String(student.user_id)));

        return (
            <section className="admin-bulk-panel admin-reset-panel">
                <div>
                    <h2>Reset Pre-test / Post-test</h2>
                    <p>
                        Topic: <strong>{topic?.topic_name || "-"}</strong>. Reset akan menghapus sesi dan hasil assessment student yang dipilih.
                    </p>
                </div>
                <div className="admin-bulk-grid admin-bulk-grid--compact">
                    <div className="admin-bulk-field">
                        <span>Assessment</span>
                        <div className="admin-check-list">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={topicReset.activity_types.includes("pre_test")}
                                    onChange={() => toggleTopicResetActivity("pre_test")}
                                />
                                <span>Pre-test</span>
                            </label>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={topicReset.activity_types.includes("post_test")}
                                    onChange={() => toggleTopicResetActivity("post_test")}
                                />
                                <span>Post-test</span>
                            </label>
                        </div>
                    </div>
                    <label>
                        Course Group
                        <select
                            value={topicReset.course_group_id}
                            onChange={(event) => updateTopicReset("course_group_id", event.target.value)}
                        >
                            <option value="">Semua group</option>
                            {groups.map((group) => (
                                <option key={group.course_group_id} value={group.course_group_id}>
                                    {group.group_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Search Student
                        <input
                            value={topicReset.search}
                            onChange={(event) => updateTopicReset("search", event.target.value)}
                            placeholder="Nama atau email"
                        />
                    </label>
                </div>
                <div className="admin-bulk-actions">
                    <span>{topicResetStudents.length} shown, {selectedCount} selected</span>
                    <button
                        type="button"
                        disabled={busy || topicResetStudents.length === 0}
                        onClick={() => setAllTopicResetStudentsSelected(!allShownSelected)}
                    >
                        {allShownSelected ? "Clear Shown" : "Select All Shown"}
                    </button>
                    <button
                        className="is-danger"
                        type="button"
                        disabled={busy || selectedCount === 0 || topicReset.activity_types.length === 0}
                        onClick={resetTopicAssessmentAttempts}
                    >
                        Reset Selected
                    </button>
                    <button
                        type="button"
                        disabled={busy}
                        onClick={() => setTopicReset({topic_id: "", course_id: "", course_group_id: "", search: "", activity_types: ["pre_test"], user_ids: []})}
                    >
                        Close
                    </button>
                </div>
                <div className="admin-mini-table">
                    {topicResetStudents.map((student) => (
                        <label key={student.user_id}>
                            <input
                                type="checkbox"
                                checked={topicReset.user_ids.map(String).includes(String(student.user_id))}
                                onChange={() => toggleTopicResetStudent(student.user_id)}
                            />
                            <span>{student.name}</span>
                            <small>{student.email}</small>
                            <b>{student.course_group_name || "No group"}</b>
                        </label>
                    ))}
                    {topicResetStudents.length === 0 && <p>Tidak ada student sesuai filter.</p>}
                </div>
            </section>
        );
    };

    const renderUserActivities = () => {
        const canDelete = String(admin?.role || "").toLowerCase() === "admin";
        const activities = userActivityData?.recent || [];
        const isSelected = (activity) => selectedUserActivities.some((selected) => selected.type === activity.activity_type_key && selected.id === activity.activity_id);
        const pageSelected = activities.length > 0 && activities.every(isSelected);
        const togglePage = (checked) => setSelectedUserActivities((current) => checked
            ? [...current, ...activities.filter((activity) => !current.some((selected) => selected.type === activity.activity_type_key && selected.id === activity.activity_id))
                .map((activity) => ({type: activity.activity_type_key, id: activity.activity_id}))]
            : current.filter((selected) => !activities.some((activity) => selected.type === activity.activity_type_key && selected.id === activity.activity_id)));
        const userOptions = [...(references.useradmin_users || [])]
            .sort((a, b) => `${a.role_name || ""}${a.name || ""}`.localeCompare(`${b.role_name || ""}${b.name || ""}`));
        const counts = userActivityData?.counts || {};
        const totalRecords = Object.values(counts).reduce((total, value) => total + Number(value || 0), 0);
        const countCards = [
            {key: "app_sessions", label: "Login Sessions"},
            {key: "active_app_sessions", label: "Active Sessions"},
            {key: "user_logs", label: "Virtual Logs"},
            {key: "individual_sessions", label: "Individual Sessions"},
            {key: "individual_answers", label: "Individual Answers"},
            {key: "group_sessions", label: "Group Sessions"},
            {key: "group_memberships", label: "Group Memberships"},
            {key: "group_answers", label: "Group Answers"},
            {key: "quiz_sessions", label: "Quiz Sessions"},
            {key: "quiz_memberships", label: "Quiz Memberships"},
            {key: "quiz_answers", label: "Quiz Answers"},
            {key: "gamification_scores", label: "XP Scores"},
        ];

        return (
            <div className="admin-custom-page">
                <div className="admin-page-header">
                    <div>
                        <h1>User Activities</h1>
                        <p>Use this for tester accounts: review activity records and clear their testing history without deleting the user.</p>
                    </div>
                    <button type="button" onClick={() => loadUserActivity()} disabled={busy || !userActivityUserId}>
                        Refresh
                    </button>
                </div>

                {message && <div className="admin-inline-message">{message}</div>}

                <section className="admin-filter-panel admin-user-activity-filter">
                    <label>
                        User
                        <select
                            disabled={busy}
                            value={userActivityUserId}
                            onChange={(event) => updateUserActivityUser(event.target.value)}
                        >
                            <option value="">Choose user</option>
                            {userOptions.map((user) => (
                                <option key={user.user_id} value={user.user_id}>
                                    {user.name} ({user.email}) - {user.role_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <span>{userActivityData?.user ? `${userActivityData.user.course_name || "-"} / ${userActivityData.user.course_group_name || "No group"}` : "Select a tester account to inspect."}</span>
                </section>

                {userActivityData?.user && (
                    <>
                        <section className="admin-user-activity-summary">
                            <article className="admin-user-activity-profile">
                                <span>{userActivityData.user.role_name}</span>
                                <h2>{userActivityData.user.name}</h2>
                                <p>{userActivityData.user.email}</p>
                                <strong>{formatAdminNumber(totalRecords)} total records</strong>
                                <button
                                    className="is-danger"
                                    type="button"
                                    disabled={busy || totalRecords === 0 || !canDelete}
                                    onClick={deleteSelectedUserActivity}
                                >
                                    Delete All Activity
                                </button>
                            </article>
                            <div className="admin-user-activity-cards">
                                {countCards.map((card) => (
                                    <article key={card.key}>
                                        <span>{card.label}</span>
                                        <strong>{formatAdminNumber(counts[card.key] || 0)}</strong>
                                    </article>
                                ))}
                            </div>
                        </section>

                        <section className="admin-data-section">
                            <div className="admin-page-header admin-page-header--compact">
                                <div>
                                    <h2>Activity History</h2>
                                    <p>{userActivityData.pagination?.total || 0} activities</p>
                                </div>
                                {canDelete && <div className="admin-bank-copy-toolbar">
                                <span>{selectedUserActivities.length}/100 selected</span>
                                <button type="button" disabled={busy || !selectedUserActivities.length} onClick={() => setSelectedUserActivities([])}>Clear Selection</button>
                                <button type="button" className="is-danger" disabled={busy || !selectedUserActivities.length || selectedUserActivities.length > 100} onClick={deleteSelectedActivityRows}>
                                    Delete Selected ({selectedUserActivities.length})
                                </button></div>}
                            </div>
                            {canDelete && <p className="admin-inline-message">Deleting a Group Activity or Fun Quiz removes the shared session, answers, results, and scores for all participants.</p>}
                            <div className="admin-table-wrap">
                                <table className="admin-data-table">
                                    <thead>
                                    <tr>
                                        {canDelete && <th><input type="checkbox" aria-label="Select all activities on this page" disabled={busy || !activities.length} checked={pageSelected} onChange={(event) => togglePage(event.target.checked)}/></th>}
                                        <th>Source</th>
                                        <th>Activity</th>
                                        <th>Status</th>
                                        <th>Date Time</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {activities.map((activity) => (
                                        <tr key={`${activity.activity_type_key}-${activity.activity_id}`}>
                                            {canDelete && <td><input type="checkbox" aria-label={`Select ${activity.source} ${activity.activity_id}`} disabled={busy} checked={isSelected(activity)} onChange={(event) => {
                                                setSelectedUserActivities((current) => event.target.checked
                                                    ? [...current, {type: activity.activity_type_key, id: activity.activity_id}]
                                                    : current.filter((selected) => selected.type !== activity.activity_type_key || selected.id !== activity.activity_id));
                                            }}/></td>}
                                            <td>{activity.source}</td>
                                            <td>{activity.title}</td>
                                            <td>{activity.status || "-"}</td>
                                            <td>{formatValue({type: "datetime"}, activity.activity_at)}</td>
                                        </tr>
                                    ))}
                                    {(!userActivityData.recent || userActivityData.recent.length === 0) && (
                                        <tr>
                                            <td colSpan={canDelete ? 5 : 4}>No activity found.</td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="admin-bank-copy-pagination">
                                <button type="button" disabled={busy || userActivityPage <= 1} onClick={() => setUserActivityPage((page) => page - 1)}>Previous</button>
                                <span>Page {userActivityData.pagination?.page || 1} of {userActivityData.pagination?.total_pages || 1}</span>
                                <button type="button" disabled={busy || userActivityPage >= (userActivityData.pagination?.total_pages || 1)} onClick={() => setUserActivityPage((page) => page + 1)}>Next</button>
                            </div>
                        </section>
                    </>
                )}
            </div>
        );
    };

    const renderChangePassword = () => (
        <>
            <div className="admin-page-header">
                <div>
                    <h1>Change Password</h1>
                    <p>Update the password for the account currently signed in.</p>
                </div>
            </div>
            {message && <div className="admin-inline-message">{message}</div>}
            <form className="admin-data-form" onSubmit={handleChangePassword}>
                <div className="admin-form-grid">
                    <label>
                        Current Password
                        <input
                            name="current_password"
                            type="password"
                            value={passwordForm.current_password}
                            onChange={handlePasswordChange}
                            autoComplete="current-password"
                            required
                        />
                    </label>
                    <label>
                        New Password
                        <input
                            name="new_password"
                            type="password"
                            minLength={8}
                            value={passwordForm.new_password}
                            onChange={handlePasswordChange}
                            autoComplete="new-password"
                            required
                        />
                    </label>
                    <label>
                        Confirm New Password
                        <input
                            name="confirm_password"
                            type="password"
                            minLength={8}
                            value={passwordForm.confirm_password}
                            onChange={handlePasswordChange}
                            autoComplete="new-password"
                            required
                        />
                    </label>
                </div>
                <button type="submit" disabled={busy}>
                    Change Password
                </button>
            </form>
        </>
    );

    const renderBankForm = () => {
        if (!activeConfig?.bankType || Object.keys(bankForm).length === 0) return null;
        const bankType = activeConfig.bankType;
        const isIndividual = bankType === "individual_questions";
        const isCase = bankType === "topic_cases" || (isIndividual && getQuestionKindFromType(bankForm.individual_question_type) === "case_study");
        return (
            <form className="admin-data-form admin-bank-form" onSubmit={saveBankItem}>
                <div>
                    <h2>{editingBankRow ? "Edit Question Bank Item" : "Add Question Bank Item"}</h2>
                    <button type="button" onClick={() => {
                        setBankForm({});
                        setEditingBankRow(null);
                    }}>
                        Cancel
                    </button>
                </div>
                <div className="admin-form-grid">
                    <label>
                        Topic
                        <select
                            value={bankForm.topic_id}
                            onChange={(event) => updateBankForm("topic_id", event.target.value)}
                            required
                        >
                            <option value="">Choose Topic</option>
                            {renderTopicOptions()}
                        </select>
                    </label>
                    {isIndividual && (
                        <label>
                            Individual Question Type
                            <select
                                value={bankForm.individual_question_type}
                                onChange={(event) => updateBankForm("individual_question_type", event.target.value)}
                            >
                                <option value="exercise_multiple_choice">Exercise - Multiple Choice</option>
                                <option value="exercise_case_study">Exercise - Case Study</option>
                                <option value="pre_test_multiple_choice">Pre-test - Multiple Choice</option>
                                <option value="post_test_multiple_choice">Post-test - Multiple Choice</option>
                            </select>
                        </label>
                    )}
                    <label>
                        Question Type
                        <input value={isCase ? "Case Study" : "Multiple Choice"} readOnly />
                    </label>
                    {isCase ? (
                        <>
                            <label>
                                {bankType === "topic_cases" ? "Case Number" : "Question Number"}
                                <input
                                    type="number"
                                    min="1"
                                    max={bankType === "topic_cases" ? 15 : undefined}
                                    value={bankType === "topic_cases" ? bankForm.case_number : bankForm.question_number}
                                    onChange={(event) => updateBankForm(bankType === "topic_cases" ? "case_number" : "question_number", event.target.value)}
                                    required
                                />
                            </label>
                            <label>
                                Case Title
                                <input
                                    value={bankForm.case_title}
                                    onChange={(event) => updateBankForm("case_title", event.target.value)}
                                    required
                                />
                            </label>
                            <label className="admin-form-wide">
                                Case Prompt
                                <textarea
                                    value={bankForm.case_prompt}
                                    onChange={(event) => updateBankForm("case_prompt", event.target.value)}
                                    rows={5}
                                    required
                                />
                            </label>
                        </>
                    ) : (
                        <>
                            <label>
                                Question Number
                                <input
                                    type="number"
                                    min="1"
                                    value={bankForm.question_number}
                                    onChange={(event) => updateBankForm("question_number", event.target.value)}
                                    required
                                />
                            </label>
                            <label className="admin-form-wide">
                                Question
                                <textarea
                                    value={bankForm.question_text}
                                    onChange={(event) => updateBankForm("question_text", event.target.value)}
                                    rows={3}
                                    required
                                />
                            </label>
                            <label className="admin-form-wide">
                                Choices (one per line, exactly 4)
                                <textarea
                                    value={bankForm.choices_text}
                                    onChange={(event) => updateBankForm("choices_text", event.target.value)}
                                    rows={4}
                                    required
                                />
                            </label>
                            <label>
                                Correct Answer
                                <select
                                    value={bankForm.correct_answer_index}
                                    onChange={(event) => updateBankForm("correct_answer_index", event.target.value)}
                                >
                                    <option value={0}>Choice 1</option>
                                    <option value={1}>Choice 2</option>
                                    <option value={2}>Choice 3</option>
                                    <option value={3}>Choice 4</option>
                                </select>
                            </label>
                            <label className="admin-form-wide">
                                Explanation
                                <textarea
                                    value={bankForm.explanation}
                                    onChange={(event) => updateBankForm("explanation", event.target.value)}
                                    rows={3}
                                />
                            </label>
                        </>
                    )}
                </div>
                <button type="submit" disabled={busy}>Save Question Bank Item</button>
            </form>
        );
    };

    const renderBankManager = () => {
        const config = BANK_CONFIGS[activeConfig.bankType];
        const isIndividualBank = activeConfig.bankType === "individual_questions";
        const bankTopicOptions = topicOptionsForCourse(bankFilters.course_id);
        const filteredBankRows = bankRows.filter((row) => (
            (!bankFilters.course_id || String(row.course_id) === String(bankFilters.course_id))
            && (!bankFilters.topic_id || String(row.topic_id) === String(bankFilters.topic_id))
            && (!isIndividualBank || !bankFilters.activity_type || row.activity_type === bankFilters.activity_type)
            && (!isIndividualBank || !bankFilters.question_kind || row.question_kind === bankFilters.question_kind)
        ));
        const totalPages = Math.max(1, Math.ceil(filteredBankRows.length / BANK_PAGE_SIZE));
        const safePage = Math.min(bankPage, totalPages);
        const pageStart = (safePage - 1) * BANK_PAGE_SIZE;
        const pageRows = filteredBankRows.slice(pageStart, pageStart + BANK_PAGE_SIZE);
        const pageIds = pageRows.map((row) => row[config.idKey]);
        const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedBankIds.map(String).includes(String(id)));
        return (
            <>
                <div className="admin-page-header">
                    <div>
                        <h1>{config.title}</h1>
                        <p>{activeConfig.description}</p>
                    </div>
                    <button type="button" onClick={openBankAddForm}>Add New Data</button>
                </div>
                {message && <div className="admin-inline-message">{message}</div>}
                {renderBankForm()}
                <div className="admin-bank-filters">
                    <label>
                        Course
                        <select
                            value={bankFilters.course_id}
                            onChange={(event) => updateBankFilter("course_id", event.target.value)}
                        >
                            <option value="">All Courses</option>
                            {(references.courses || []).map((course) => (
                                <option key={course.course_id} value={course.course_id}>
                                    {course.course_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Topic
                        <select
                            value={bankFilters.topic_id}
                            onChange={(event) => updateBankFilter("topic_id", event.target.value)}
                        >
                            <option value="">All Topics</option>
                            {bankTopicOptions.map((topic) => (
                                <option key={topic.topic_id} value={topic.topic_id}>
                                    {topic.course_name} - {topic.topic_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    {isIndividualBank && (
                        <>
                            <label>
                                Activity
                                <select
                                    value={bankFilters.activity_type}
                                    onChange={(event) => updateBankFilter("activity_type", event.target.value)}
                                >
                                    <option value="">All Activities</option>
                                    <option value="exercise">Exercise</option>
                                    <option value="pre_test">Pre-test</option>
                                    <option value="post_test">Post-test</option>
                                </select>
                            </label>
                            <label>
                                Type
                                <select
                                    value={bankFilters.question_kind}
                                    onChange={(event) => updateBankFilter("question_kind", event.target.value)}
                                >
                                    <option value="">All Types</option>
                                    <option value="multiple_choice">Multiple Choice</option>
                                    <option value="case_study">Case Study</option>
                                </select>
                            </label>
                        </>
                    )}
                </div>
                <div className="admin-bulk-actions admin-bank-bulk-actions">
                    <span>{selectedBankIds.length} selected</span>
                    <button
                        type="button"
                        disabled={busy || pageIds.length === 0}
                        onClick={() => setAllPageBankSelected(!allPageSelected, pageIds)}
                    >
                        {allPageSelected ? "Clear Page" : "Select Page"}
                    </button>
                    <button
                        className="is-danger"
                        type="button"
                        disabled={busy || selectedBankIds.length === 0}
                        onClick={() => bulkDeleteBankItems()}
                    >
                        Delete Selected
                    </button>
                </div>
                <div className="admin-pagination">
                    <span>
                        Showing {filteredBankRows.length === 0 ? 0 : pageStart + 1}-{Math.min(pageStart + BANK_PAGE_SIZE, filteredBankRows.length)} of {filteredBankRows.length}
                    </span>
                    <div>
                        <button
                            type="button"
                            onClick={() => setBankPage((current) => Math.max(1, current - 1))}
                            disabled={safePage <= 1}
                        >
                            Previous
                        </button>
                        <b>Page {safePage} of {totalPages}</b>
                        <button
                            type="button"
                            onClick={() => setBankPage((current) => Math.min(totalPages, current + 1))}
                            disabled={safePage >= totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
                <div className="admin-table-wrap">
                    <table className="admin-data-table">
                        <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    checked={allPageSelected}
                                    onChange={(event) => setAllPageBankSelected(event.target.checked, pageIds)}
                                    aria-label="Select all question bank rows on this page"
                                />
                            </th>
                            {config.columns.map((column) => <th key={column.key}>{column.label}</th>)}
                            <th>Detail</th>
                            <th>Action</th>
                        </tr>
                        </thead>
                        <tbody>
                        {pageRows.map((row) => (
                            <tr key={row[config.idKey]}>
                                <td>
                                    <input
                                        type="checkbox"
                                        checked={selectedBankIds.map(String).includes(String(row[config.idKey]))}
                                        onChange={() => toggleBankSelection(row[config.idKey])}
                                        aria-label={`Select ${row.question_text || row.case_title || "question bank item"}`}
                                    />
                                </td>
                                {config.columns.map((column) => (
                                    <td key={column.key}>
                                        {column.key === "question_text" ? (
                                            <MarkdownText>{row.question_text || row.case_title}</MarkdownText>
                                        ) : formatValue(column, row[column.key])}
                                    </td>
                                ))}
                                <td>
                                    {row.choices?.length
                                        ? renderChoicesPreview(row.choices, row.correct_answer_index)
                                        : row.case_prompt || row.explanation
                                            ? <MarkdownText>{row.case_prompt || row.explanation}</MarkdownText>
                                            : "-"}
                                </td>
                                <td>
                                    <div className="admin-row-actions">
                                        <button type="button" onClick={() => openBankEditForm(row)}>Edit</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {filteredBankRows.length === 0 && (
                            <tr>
                                <td colSpan={config.columns.length + 3}>
                                    {busy ? "Loading data..." : "No data found."}
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    const renderCoverageCount = (value) => {
        const count = Number(value || 0);
        return (
            <span className={`coverage-count ${count > 0 ? "is-ready" : "is-empty"}`}>
                {formatAdminNumber(count)}
            </span>
        );
    };

    const submitBankCopy = async (event) => {
        event.preventDefault();
        if (!bankCopyIds.length || bankCopySaving || bankCopyLoading) return;
        const source = COPY_BANK_OPTIONS.find((option) => option.value === bankCopy.source_bank);
        const target = COPY_BANK_OPTIONS.find((option) => option.value === bankCopy.target_bank);
        if (!window.confirm(`Salin ${bankCopyIds.length} soal dari ${source.label} ke ${target.label} dengan urutan acak?`)) return;
        setBankCopySaving(true);
        setMessage("");
        try {
            const data = await apiPost("/admin/question-bank/copy", {
                source_bank: bankCopy.source_bank, target_bank: bankCopy.target_bank,
                source_topic_id: bankCopy.source_topic_id, target_topic_id: bankCopy.target_topic_id,
                item_ids: bankCopyIds,
            });
            setMessage(data.message || "Gagal menyalin soal.");
            if (data.data) setBankCopyIds([]);
        } catch (error) {
            setMessage("Status penyalinan belum dapat dipastikan karena koneksi terputus. Periksa bank tujuan sebelum mencoba lagi.");
        } finally {
            setBankCopySaving(false);
        }
    };

    const renderBankCopy = () => {
        const source = COPY_BANK_OPTIONS.find((option) => option.value === bankCopy.source_bank);
        const destinations = COPY_BANK_OPTIONS.filter((option) => option.kind === source.kind && option.value !== source.value);
        const pages = Math.max(1, Math.ceil(bankCopyRows.length / BANK_PAGE_SIZE));
        const pageRows = bankCopyRows.slice((bankCopyPage - 1) * BANK_PAGE_SIZE, bankCopyPage * BANK_PAGE_SIZE);
        const locked = bankCopySaving;
        return (
            <>
                <div className="admin-page-header"><h1>Copy Question Bank</h1></div>
                {message && <div className="admin-inline-message" role="status">{message}</div>}
                {bankCopyError && <div className="admin-inline-message" role="alert">{bankCopyError}</div>}
                <form className="admin-data-form admin-bank-copy-form" onSubmit={submitBankCopy}>
                    <div className="admin-form-grid">
                        <label>Source Course
                            <select required disabled={locked} value={bankCopy.source_course_id} onChange={(event) => {
                                setBankCopyRows([]); setBankCopyIds([]); setMessage("");
                                setBankCopy((current) => ({...current, source_course_id: event.target.value, source_topic_id: "", target_course_id: event.target.value, target_topic_id: ""}));
                            }}>
                                <option value="">Select course</option>
                                {(references.courses || []).map((course) => <option key={course.course_id} value={course.course_id}>{course.course_name}</option>)}
                            </select>
                        </label>
                        <label>Destination Course
                            <select required disabled={locked} value={bankCopy.target_course_id} onChange={(event) => setBankCopy((current) => ({...current, target_course_id: event.target.value, target_topic_id: ""}))}>
                                <option value="">Select course</option>
                                {(references.courses || []).map((course) => <option key={course.course_id} value={course.course_id}>{course.course_name}</option>)}
                            </select>
                        </label>
                        <label>Source Topic
                            <select required disabled={locked || !bankCopy.source_course_id} value={bankCopy.source_topic_id} onChange={(event) => {
                                setBankCopyRows([]); setBankCopyIds([]); setMessage("");
                                setBankCopy((current) => ({...current, source_topic_id: event.target.value,
                                    target_topic_id: current.target_course_id === current.source_course_id ? event.target.value : current.target_topic_id}));
                            }}>
                                <option value="">Select topic</option>
                                {(references.topics || []).filter((topic) => String(topic.course_id) === String(bankCopy.source_course_id)).map((topic) => <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>)}
                            </select>
                        </label>
                        <label>Destination Topic
                            <select required disabled={locked || !bankCopy.target_course_id} value={bankCopy.target_topic_id} onChange={(event) => setBankCopy((current) => ({...current, target_topic_id: event.target.value}))}>
                                <option value="">Select topic</option>
                                {(references.topics || []).filter((topic) => String(topic.course_id) === String(bankCopy.target_course_id)).map((topic) => <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>)}
                            </select>
                        </label>
                        <label>Source Bank
                            <select disabled={locked} value={bankCopy.source_bank} onChange={(event) => {
                                const next = COPY_BANK_OPTIONS.find((option) => option.value === event.target.value);
                                const compatible = COPY_BANK_OPTIONS.filter((option) => option.kind === next.kind && option.value !== next.value);
                                setBankCopyRows([]); setBankCopyIds([]); setMessage("");
                                setBankCopy((current) => ({...current, source_bank: next.value,
                                    target_bank: compatible.some((option) => option.value === current.target_bank) ? current.target_bank : compatible[0].value}));
                            }}>
                                {COPY_BANK_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                        </label>
                        <label>Destination Bank
                            <select disabled={locked} value={bankCopy.target_bank} onChange={(event) => setBankCopy((current) => ({...current, target_bank: event.target.value}))}>
                                {destinations.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                        </label>
                    </div>
                    <div className="admin-bank-copy-toolbar">
                        <label className="admin-bank-copy-select">
                            <input type="checkbox" disabled={locked || bankCopyLoading || !bankCopyRows.length}
                                checked={bankCopyRows.length > 0 && bankCopyIds.length === bankCopyRows.length}
                                onChange={(event) => setBankCopyIds(event.target.checked ? bankCopyRows.map((row) => row.question_id || row.case_id) : [])}/>
                            Select all ({bankCopyRows.length})
                        </label>
                        <span>{bankCopyIds.length} selected</span>
                        <button type="submit" disabled={locked || bankCopyLoading || !bankCopyIds.length || !bankCopy.target_topic_id}>
                            {locked ? "Copying..." : "Copy Selected Questions"}
                        </button>
                    </div>
                    <div className="admin-table-wrap">
                        <table className="admin-bank-copy-table">
                            <thead><tr><th>Select</th><th>Number</th><th>{source.kind === "case_study" ? "Case Study" : "Question"}</th></tr></thead>
                            <tbody>
                                {pageRows.map((row) => {
                                    const id = row.question_id || row.case_id;
                                    return <tr key={id}>
                                        <td><input type="checkbox" disabled={locked} checked={bankCopyIds.includes(id)} aria-label={`Select question ${row.question_number || row.case_number}`}
                                            onChange={(event) => setBankCopyIds((current) => event.target.checked ? [...current, id] : current.filter((item) => item !== id))}/></td>
                                        <td>{row.question_number || row.case_number}</td>
                                        <td><details><summary>{row.case_title || `Question ${row.question_number}`}</summary>
                                            <MarkdownText>{row.case_prompt || row.question_text}</MarkdownText>
                                            {row.question_type === "multiple_choice" && renderChoicesPreview(row.choices, row.correct_answer_index)}
                                        </details></td>
                                    </tr>;
                                })}
                                {!pageRows.length && <tr><td colSpan={3}>{bankCopyLoading ? "Loading questions..." : bankCopy.source_topic_id ? "No questions available." : "Select a source topic."}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <div className="admin-bank-copy-pagination">
                        <button type="button" disabled={locked || bankCopyPage <= 1} onClick={() => setBankCopyPage((page) => page - 1)} aria-label="Previous page">Previous</button>
                        <span>Page {bankCopyPage} of {pages}</span>
                        <button type="button" disabled={locked || bankCopyPage >= pages} onClick={() => setBankCopyPage((page) => page + 1)} aria-label="Next page">Next</button>
                    </div>
                </form>
            </>
        );
    };

    const renderQuestionBankCoverage = () => {
        const selectedCourse = (references.courses || []).find((course) => (
            String(course.course_id) === String(questionBankCoverageCourseId)
        ));
        const totalTopics = questionBankCoverageRows.length;
        const completeTopics = questionBankCoverageRows.filter((row) => (
            Number(row.pre_test_count || 0) > 0
            && Number(row.post_test_count || 0) > 0
            && Number(row.individual_question_count || 0) > 0
            && Number(row.individual_case_count || 0) > 0
            && Number(row.group_case_count || 0) > 0
            && Number(row.quiz_count || 0) > 0
        )).length;

        return (
            <>
                <div className="admin-page-header">
                    <div>
                        <h1>Question Bank Monitor</h1>
                        <p>Check question and case availability per topic for the selected course.</p>
                    </div>
                </div>
                {message && <div className="admin-inline-message">{message}</div>}

                <section className="admin-bank-filters coverage-filters">
                    <label>
                        Course
                        <select
                            value={questionBankCoverageCourseId}
                            onChange={(event) => updateQuestionBankCoverageCourse(event.target.value)}
                        >
                            <option value="">Choose Course</option>
                            {(references.courses || []).map((course) => (
                                <option key={course.course_id} value={course.course_id}>
                                    {course.course_name}
                                </option>
                            ))}
                        </select>
                    </label>
                </section>

                {questionBankCoverageCourseId && (
                    <div className="coverage-summary">
                        <article>
                            <span>Course</span>
                            <strong>{selectedCourse?.course_name || "-"}</strong>
                        </article>
                        <article>
                            <span>Topics</span>
                            <strong>{formatAdminNumber(totalTopics)}</strong>
                        </article>
                        <article>
                            <span>Complete Topics</span>
                            <strong>{formatAdminNumber(completeTopics)}</strong>
                        </article>
                    </div>
                )}

                <div className="admin-table-wrap">
                    <table className="admin-data-table coverage-table">
                        <thead>
                        <tr>
                            <th>Week</th>
                            <th>Topic</th>
                            <th>Pre-test</th>
                            <th>Post-test</th>
                            <th>Individual MC</th>
                            <th>Individual Case</th>
                            <th>Group Case</th>
                            <th>Quiz</th>
                        </tr>
                        </thead>
                        <tbody>
                        {!questionBankCoverageCourseId && (
                            <tr>
                                <td colSpan={8}>Choose a course to display topic coverage.</td>
                            </tr>
                        )}
                        {questionBankCoverageCourseId && questionBankCoverageRows.map((row) => (
                            <tr key={row.topic_id}>
                                <td>{row.week || "-"}</td>
                                <td>
                                    <strong>{row.topic_name}</strong>
                                </td>
                                <td>{renderCoverageCount(row.pre_test_count)}</td>
                                <td>{renderCoverageCount(row.post_test_count)}</td>
                                <td>{renderCoverageCount(row.individual_question_count)}</td>
                                <td>{renderCoverageCount(row.individual_case_count)}</td>
                                <td>{renderCoverageCount(row.group_case_count)}</td>
                                <td>{renderCoverageCount(row.quiz_count)}</td>
                            </tr>
                        ))}
                        {questionBankCoverageCourseId && questionBankCoverageRows.length === 0 && (
                            <tr>
                                <td colSpan={8}>{busy ? "Loading data..." : "No topics found for this course."}</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    const renderQuestionBank = () => {
        const materialTopicOptions = topicOptionsForCourse("");
        const visibleMaterials = materialTopicFilter
            ? materials.filter((material) => String(material.topic_id) === String(materialTopicFilter))
            : [];
        const selectedTopicMaterials = questionSettings.topic_id
            ? materials.filter((material) => String(material.topic_id) === String(questionSettings.topic_id))
            : [];
        const questionType = getBankQuestionType();
        const isCaseBank = questionType === "case_study";

        return (
            <div className="admin-question-bank">
                <div className="admin-page-header">
                    <div>
                        <h1>Material & Question Bank</h1>
                        <p>Upload text material once, cache a digest, generate drafts from that digest, then review before saving.</p>
                    </div>
                </div>

                {message && <div className="admin-inline-message">{message}</div>}

                <section className="admin-qb-grid">
                    <form className="admin-data-form admin-qb-panel-form" onSubmit={saveMaterial}>
                        <div>
                            <h2>{editingMaterial ? "Edit Course Material" : "Add Course Material"}</h2>
                            {editingMaterial && (
                                <button type="button" onClick={() => {
                                    setEditingMaterial(null);
                                    setMaterialForm({topic_id: "", title: "", content_text: ""});
                                }}>
                                    Cancel
                                </button>
                            )}
                        </div>
                        <label>
                            Topic
                            <select
                                value={materialForm.topic_id}
                                onChange={(event) => setMaterialForm({...materialForm, topic_id: event.target.value})}
                                required
                            >
                                <option value="">Choose Topic</option>
                                {renderTopicOptions()}
                            </select>
                        </label>
                        <label>
                            Material Title
                            <input
                                value={materialForm.title}
                                onChange={(event) => setMaterialForm({...materialForm, title: event.target.value})}
                                required
                            />
                        </label>
                        <label>
                            Text Material
                            <textarea
                                value={materialForm.content_text}
                                onChange={(event) => setMaterialForm({...materialForm, content_text: event.target.value})}
                                required
                                rows={10}
                            />
                        </label>
                        <button type="submit" disabled={busy}>Save Material</button>
                    </form>

                    <section className="admin-material-list">
                        <div className="admin-material-list-header">
                            <h2>Saved Materials</h2>
                            <label>
                                Topic
                                <select
                                    value={materialTopicFilter}
                                    onChange={(event) => updateMaterialTopicFilter(event.target.value)}
                                >
                                    <option value="">Choose Topic</option>
                                    {materialTopicOptions.map((topic) => (
                                        <option key={topic.topic_id} value={topic.topic_id}>
                                            {topic.course_name} - {topic.topic_name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        {!materialTopicFilter && <p>Choose a topic to display saved materials.</p>}
                        {materialTopicFilter && visibleMaterials.length === 0 && <p>No material found for this topic.</p>}
                        {visibleMaterials.map((material) => (
                            <article key={material.material_id}>
                                <div>
                                    <strong>{material.title}</strong>
                                    <span>{material.course_name} / {material.topic_name}</span>
                                    <small>{material.content_token_estimate} estimated tokens</small>
                                    <b className={`admin-digest admin-digest--${material.digest_status}`}>
                                        Digest: {material.digest_status}
                                    </b>
                                </div>
                                <div>
                                    <button type="button" onClick={() => editMaterial(material)}>Edit</button>
                                    <button type="button" onClick={() => generateDigest(material)} disabled={busy}>
                                        Generate Digest
                                    </button>
                                    <button type="button" onClick={() => removeMaterial(material)}>Delete</button>
                                </div>
                            </article>
                        ))}
                    </section>
                </section>

                <form className="admin-data-form admin-qb-generate-form" onSubmit={generateDrafts}>
                    <div>
                        <h2>Generate Question Drafts</h2>
                    </div>
                    <div className="admin-form-grid">
                        <label>
                            Topic
                            <select
                                value={questionSettings.topic_id}
                                onChange={(event) => updateQuestionSettings("topic_id", event.target.value)}
                                required
                            >
                                <option value="">Choose Topic</option>
                                {renderTopicOptions()}
                            </select>
                        </label>
                        <div className="admin-material-picker">
                            <strong>Materials</strong>
                            {!questionSettings.topic_id && <span>Choose topic first.</span>}
                            {questionSettings.topic_id && selectedTopicMaterials.length === 0 && <span>No material found for this topic.</span>}
                            {selectedTopicMaterials.map((material) => (
                                <label key={material.material_id}>
                                    <input
                                        type="checkbox"
                                        checked={(questionSettings.material_ids || []).map(String).includes(String(material.material_id))}
                                        onChange={() => toggleMaterialSelection(material.material_id)}
                                    />
                                    <span>{material.title} ({material.digest_status})</span>
                                </label>
                            ))}
                        </div>
                        <label>
                            Bank
                            <select
                                value={getQuestionBankTarget(questionSettings)}
                                onChange={(event) => updateQuestionBankTarget(event.target.value)}
                            >
                                {QUESTION_BANK_TARGETS.map((target) => (
                                    <option key={target.value} value={target.value}>
                                        {target.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {questionSettings.bank_type === "individual_questions" && (
                            <label>
                                Saved As
                                <input
                                    value={`${getActivityFromType(questionSettings.individual_question_type)} / ${getQuestionKindFromType(questionSettings.individual_question_type)}`}
                                    readOnly
                                />
                            </label>
                        )}
                        <label>
                            Generated Question Type
                            <input value={questionType === "case_study" ? "Case Study" : "Multiple Choice"} readOnly />
                        </label>
                        <label>
                            OpenAI Model
                            <select
                                value={questionSettings.openai_model}
                                onChange={(event) => updateQuestionSettings("openai_model", event.target.value)}
                            >
                                {OPENAI_MODEL_OPTIONS.map((model) => (
                                    <option key={model.value} value={model.value}>
                                        {model.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label>
                            Count
                            <input
                                type="number"
                                min="1"
                                max={isCaseBank ? 15 : 20}
                                value={questionSettings.count}
                                onChange={(event) => updateQuestionSettings("count", event.target.value)}
                            />
                        </label>
                        <label className="admin-form-wide">
                            Specific Instruction / Prompt
                            <textarea
                                value={questionSettings.custom_instruction}
                                onChange={(event) => updateQuestionSettings("custom_instruction", event.target.value)}
                                maxLength={8000}
                                placeholder="Optional. Use [PRESERVE_CODE] when material contains source code. Example: [PRESERVE_CODE] Fokus pada debugging dan output program."
                                rows={4}
                            />
                            <small className="admin-field-hint">
                                Keyword: <code>[PRESERVE_CODE]</code> keeps code blocks, indentation, and line breaks during question generation.
                                {" "}{questionSettings.custom_instruction.length}/8000 characters.
                            </small>
                        </label>
                        <div className="admin-instruction-presets admin-form-wide">
                            <div className="admin-instruction-presets__header">
                                <strong>Instruction Presets</strong>
                                <span>Save a long reusable instruction, then click a preset to apply it to the prompt above.</span>
                            </div>
                            <div className="admin-instruction-presets__form">
                                <label>
                                    Preset Name
                                    <input
                                        value={instructionPresetForm.preset_name}
                                        onChange={(event) => setInstructionPresetForm((current) => ({
                                            ...current,
                                            preset_name: event.target.value,
                                        }))}
                                        maxLength={120}
                                        placeholder="Example: Code case study"
                                    />
                                </label>
                                <label>
                                    Preset Instruction
                                    <textarea
                                        value={instructionPresetForm.instruction_text}
                                        onChange={(event) => setInstructionPresetForm((current) => ({
                                            ...current,
                                            instruction_text: event.target.value,
                                        }))}
                                        maxLength={8000}
                                        placeholder="Example: [PRESERVE_CODE] untuk 10 soal, buat studi kasus dengan potongan kode sederhana..."
                                        rows={4}
                                    />
                                    <small>{instructionPresetForm.instruction_text.length}/8000 characters</small>
                                </label>
                                <button
                                    type="button"
                                    className="admin-instruction-presets__secondary"
                                    onClick={() => setInstructionPresetForm((current) => ({
                                        ...current,
                                        instruction_text: questionSettings.custom_instruction,
                                    }))}
                                    disabled={!questionSettings.custom_instruction.trim()}
                                >
                                    Copy Current Prompt
                                </button>
                                <button
                                    type="button"
                                    onClick={saveInstructionPreset}
                                    disabled={busy || !instructionPresetForm.preset_name.trim() || !instructionPresetForm.instruction_text.trim()}
                                >
                                    {editingInstructionPresetId ? "Update Preset" : "Save Preset"}
                                </button>
                                {editingInstructionPresetId && (
                                    <button
                                        type="button"
                                        className="admin-instruction-presets__secondary"
                                        disabled={busy}
                                        onClick={() => {
                                            setEditingInstructionPresetId(null);
                                            setInstructionPresetForm({preset_name: "", instruction_text: ""});
                                        }}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                            <div className="admin-instruction-presets__list">
                                {instructionPresets.length === 0 && <span>No presets saved yet.</span>}
                                {instructionPresets.map((preset) => (
                                    <div key={preset.preset_id} className="admin-instruction-preset">
                                        <button type="button" onClick={() => applyInstructionPreset(preset)}>
                                            {preset.preset_name}
                                        </button>
                                        <button
                                            type="button"
                                            className="admin-instruction-preset__delete"
                                            disabled={busy}
                                            aria-label={`Edit ${preset.preset_name}`}
                                            onClick={() => {
                                                setEditingInstructionPresetId(preset.preset_id);
                                                setInstructionPresetForm({
                                                    preset_name: preset.preset_name,
                                                    instruction_text: preset.instruction_text,
                                                });
                                            }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            type="button"
                                            className="admin-instruction-preset__delete"
                                            disabled={busy}
                                            onClick={() => deleteInstructionPreset(preset)}
                                            aria-label={`Delete ${preset.preset_name}`}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <button type="submit" disabled={busy || !questionSettings.topic_id || !questionSettings.material_ids?.length}>
                        Generate Drafts
                    </button>
                    {draftMeta && (
                        <p className="admin-qb-note">
                            Model: {draftMeta.model}. Source: {draftMeta.used_digest ? "cached digest/material mix" : "raw material excerpts"}. Materials: {draftMeta.material_count}.
                        </p>
                    )}
                </form>

                {drafts.length > 0 && (
                    <section className="admin-draft-review">
                        <div className="admin-page-header">
                            <div>
                                <h1>Review Drafts</h1>
                                <p>Edit before saving. AI source excerpts are shown to help validate grounding.</p>
                            </div>
                            <button type="button" onClick={saveDrafts} disabled={busy}>Save Reviewed Drafts</button>
                        </div>
                        {drafts.map((draft, index) => (
                            <article key={index} className="admin-draft-card">
                                <header>
                                    <div>
                                        <span>Draft {index + 1}</span>
                                        <b>{draft.question_type === "case_study" ? "Case Study" : "Multiple Choice"}</b>
                                    </div>
                                    <button
                                        className="admin-draft-remove"
                                        type="button"
                                        onClick={() => removeDraft(index)}
                                        disabled={busy}
                                    >
                                        Remove Draft
                                    </button>
                                </header>
                                {isCaseBank ? (
                                    <>
                                        <label>
                                            Case Number
                                            <input
                                                type="number"
                                                min="1"
                                                max="15"
                                                value={draft.case_number}
                                                onChange={(event) => updateDraft(index, "case_number", event.target.value)}
                                            />
                                        </label>
                                        <label>
                                            Case Title
                                            <input
                                                value={draft.case_title}
                                                onChange={(event) => updateDraft(index, "case_title", event.target.value)}
                                            />
                                        </label>
                                        <label>
                                            Case Prompt
                                            <textarea
                                                value={draft.case_prompt}
                                                onChange={(event) => updateDraft(index, "case_prompt", event.target.value)}
                                                rows={5}
                                            />
                                        </label>
                                        <div className="admin-draft-preview">
                                            <strong>Case Preview</strong>
                                            <MarkdownText>{draft.case_prompt}</MarkdownText>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <label>
                                            Question Number
                                            <input
                                                type="number"
                                                value={draft.question_number}
                                                onChange={(event) => updateDraft(index, "question_number", event.target.value)}
                                            />
                                        </label>
                                        <label>
                                            Question
                                            <textarea
                                                value={draft.question_text}
                                                onChange={(event) => updateDraft(index, "question_text", event.target.value)}
                                                rows={3}
                                            />
                                        </label>
                                        <div className="admin-draft-preview">
                                            <strong>Question Preview</strong>
                                            <MarkdownText>{draft.question_text}</MarkdownText>
                                        </div>
                                        <div className="admin-choice-grid">
                                            {(draft.choices || []).map((choice, choiceIndex) => (
                                                <label key={choiceIndex}>
                                                    Choice {choiceIndex + 1}
                                                    <input
                                                        value={choice}
                                                        onChange={(event) => updateDraftChoice(index, choiceIndex, event.target.value)}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                        <label>
                                            Correct Answer
                                            <select
                                                value={draft.correct_answer_index}
                                                onChange={(event) => updateDraft(index, "correct_answer_index", event.target.value)}
                                            >
                                                <option value={0}>Choice 1</option>
                                                <option value={1}>Choice 2</option>
                                                <option value={2}>Choice 3</option>
                                                <option value={3}>Choice 4</option>
                                            </select>
                                        </label>
                                        <label>
                                            Explanation
                                            <textarea
                                                value={draft.explanation}
                                                onChange={(event) => updateDraft(index, "explanation", event.target.value)}
                                                rows={3}
                                            />
                                        </label>
                                    </>
                                )}
                                <label>
                                    Source Excerpt for Validation
                                    <textarea
                                        value={draft.source_excerpt}
                                        onChange={(event) => updateDraft(index, "source_excerpt", event.target.value)}
                                        rows={2}
                                    />
                                </label>
                            </article>
                        ))}
                    </section>
                )}
            </div>
        );
    };

    if (loading) {
        return <main className="admin-login-page"><div className="admin-login-card">Loading admin...</div></main>;
    }

    if (!admin) {
        return (
            <main className="admin-login-page">
                <section className="admin-login-card">
                    <span>GamifyIt Admin</span>
                    <h1>Sign in</h1>
                    <p>Use an admin or instructor account to manage GamifyIt.</p>
                    {message && <div className="admin-alert">{message}</div>}
                    <form onSubmit={handleLogin}>
                        <label>
                            Username
                            <input
                                name="username"
                                value={loginForm.username}
                                onChange={handleLoginChange}
                                autoComplete="username"
                                required
                            />
                        </label>
                        <label>
                            Password
                            <input
                                name="password"
                                type="password"
                                value={loginForm.password}
                                onChange={handleLoginChange}
                                autoComplete="current-password"
                                required
                            />
                        </label>
                        <button type="submit" disabled={busy || !loginForm.username || !loginForm.password}>
                            Login
                        </button>
                    </form>
                </section>
            </main>
        );
    }

    return (
        <main className="admin-shell">
            <aside className="admin-sidebar">
                <div className="admin-brand">
                    <strong>GamifyIt</strong>
                    <span>Admin Console</span>
                </div>
                <nav>
                    {visibleMenuGroups.map((group) => (
                        <section key={group.key}>
                            <button
                                className="admin-menu-parent"
                                type="button"
                                onClick={() => toggleMenu(group.key)}
                            >
                                <span>{group.label}</span>
                                <b>{openMenus[group.key] ? "-" : "+"}</b>
                            </button>
                            {openMenus[group.key] && group.items.map((item) => (
                                <button
                                    className={`admin-menu-child ${location.pathname === item.path ? "is-active" : ""}`}
                                    key={item.path}
                                    type="button"
                                    onClick={() => navigate(item.path)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </section>
                    ))}
                </nav>
            </aside>

            <section className="admin-main">
                <header className="admin-topbar">
                    <div className="admin-quick-actions">
                        <button type="button" onClick={() => navigate(DEFAULT_PATH)}>Dashboard</button>
                        <button type="button" onClick={() => navigate("/courseadmin")}>Course Master</button>
                    </div>
                    <div className="admin-account">
                        <span>{admin.username}</span>
                        <strong>{admin.role}</strong>
                        <button type="button" onClick={() => navigate("/adminpassword")} disabled={busy}>
                            Change Password
                        </button>
                        <button type="button" onClick={handleLogout} disabled={busy}>
                            Logout
                        </button>
                    </div>
                </header>

                <div className="admin-breadcrumb">
                    Admin / {activeConfig ? activeConfig.label : "Dashboard"}
                </div>

                <section className="admin-page-container">
                    {!activeConfig ? (
                        renderInstructorDashboard()
                    ) : activeConfig.custom === "questionBank" ? (
                        renderQuestionBank()
                    ) : activeConfig.custom === "questionBankCoverage" ? (
                        renderQuestionBankCoverage()
                    ) : activeConfig.custom === "bankManager" ? (
                        renderBankManager()
                    ) : activeConfig.custom === "bankCopy" ? (
                        renderBankCopy()
                    ) : activeConfig.custom === "userActivities" ? (
                        renderUserActivities()
                    ) : activeConfig.custom === "changePassword" ? (
                        renderChangePassword()
                    ) : (
                        <>
                            <div className="admin-page-header">
                                <div>
                                    <h1>{activeConfig.label}</h1>
                                    <p>{activeConfig.description}</p>
                                </div>
                                {activeConfig.canAdd && (
                                    <button type="button" onClick={openAddForm}>
                                        Add New Data
                                    </button>
                                )}
                            </div>

                            {message && <div className="admin-inline-message">{message}</div>}

                            {renderStudentBulkPanel()}
                            {renderTopicCourseFilter()}
                            {renderTopicResetPanel()}

                            {(Object.keys(formData).length > 0 || editingRow) && (
                                <form className="admin-data-form" onSubmit={handleSave}>
                                    <div>
                                        <h2>{editingRow ? "Edit Data" : "Add New Data"}</h2>
                                        <button type="button" onClick={() => {
                                            setEditingRow(null);
                                            setFormData({});
                                        }}>
                                            Cancel
                                        </button>
                                    </div>
                                    <div className="admin-form-grid">
                                        {activeConfig.fields.filter(isFormFieldVisible).map((field) => (
                                            <label key={field.key}>
                                                {field.label}
                                                {renderField(field)}
                                            </label>
                                        ))}
                                    </div>
                                    <button type="submit" disabled={busy}>
                                        Save Data
                                    </button>
                                </form>
                            )}

                            <div className="admin-table-wrap">
                                <table className="admin-data-table">
                                    <thead>
                                    <tr>
                                        {activeConfig.resource === "students" && (
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    checked={displayedRows.length > 0 && selectedStudentIds.length === displayedRows.length}
                                                    onChange={(event) => setAllDisplayedStudentsSelected(event.target.checked)}
                                                    aria-label="Select all shown students"
                                                />
                                            </th>
                                        )}
                                        {activeConfig.tableColumns.map((column) => (
                                            <th key={column.key}>{column.label}</th>
                                        ))}
                                        <th>Action</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {displayedRows.map((row) => (
                                        <tr key={row[activeConfig.idKey]}>
                                            {activeConfig.resource === "students" && (
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedStudentIds.map(String).includes(String(row.user_id))}
                                                        onChange={() => toggleStudentSelection(row.user_id)}
                                                        aria-label={`Select ${row.name}`}
                                                    />
                                                </td>
                                            )}
                                            {activeConfig.tableColumns.map((column) => (
                                                <td key={column.key}>
                                                    {column.type === "color" ? (
                                                        <span className="admin-color-cell">
                                                            <i style={{background: row[column.key]}} />
                                                            {row[column.key]}
                                                        </span>
                                                    ) : formatValue(column, column.type === "setting_value" ? row : row[column.key])}
                                                </td>
                                            ))}
                                            <td>
                                                <div className="admin-row-actions">
                                                    <button type="button" onClick={() => openEditForm(row)}>
                                                        Edit
                                                    </button>
                                                    {activeConfig.resource === "topics" && (
                                                        <button type="button" onClick={() => openTopicAssessmentReset(row)}>
                                                            Reset Test
                                                        </button>
                                                    )}
                                                    {activeConfig.canDelete && !(activeConfig.resource === "useradmins" && String(row.username || "").toLowerCase() === "admin") && (
                                                        <button type="button" onClick={() => handleDelete(row)}>
                                                            Delete
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {displayedRows.length === 0 && (
                                        <tr>
                                            <td colSpan={activeConfig.tableColumns.length + 1 + (activeConfig.resource === "students" ? 1 : 0)}>
                                                {busy ? "Loading data..." : "No data found."}
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </section>
            </section>
            {showScrollTop && (
                <button
                    className="admin-scroll-top"
                    type="button"
                    onClick={scrollToTop}
                    aria-label="Scroll to top"
                >
                    ↑ Top
                </button>
            )}
        </main>
    );
};

export default AdminPage;
